/* eslint-disable react/prop-types */
/**
 * /projects/workbench/:did — Iter 236.
 *
 * The control center for a single workbench draft. Shows the 3-stage
 * progress strip at the top, the brainstorm chat embedded inline, and
 * compact plan/estimate panels that deep-link to the existing planner
 * and estimator pages once the draft is at that stage.
 *
 * Trust Contract: no modals. Promote / back / archive / rename / link-
 * customer all happen inline.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Sparkles, ClipboardList, Calculator, Loader2,
  ArrowRight, CheckCircle2, Edit3, RotateCcw, AlertTriangle, Save, BookmarkPlus,
  ExternalLink, Users, Copy, FileText, Trash2, Eye,
  MessageSquare, ChevronDown, ChevronUp, AlertOctagon,
  Plus, ArrowUp, ArrowDown, Wand2, Check, X, Columns, RefreshCw,
  ImagePlus, FileImage, GripVertical, Link2, Unlink2,
} from "lucide-react";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
  SortableContext, useSortable, arrayMove,
  verticalListSortingStrategy, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import BomPanel from "../components/workbench/BomPanel";
import SystemMapPanel from "../components/workbench/SystemMapPanel";
import api, { formatErr, API } from "../lib/api";
import { toast } from "../components/ui/sonner";
import HidroMarkdown from "../components/HidroMarkdown";
import VoiceDictation from "../components/VoiceDictation";
import ChatComposer from "../components/chat/ChatComposer";
import ChatMessage from "../components/chat/ChatMessage";
import VisionAnalysisChip from "../components/chat/VisionAnalysisChip";
import {
  ReportInclusionChip,
  ReportAttachmentsPanel,
} from "../components/workbench/ReportInclusionControls";
import ProposalAttachmentsUploader from "../components/workbench/ProposalAttachmentsUploader";
import { PresetInsertButton, PresetLibraryDialog } from "../components/workbench/PromptPresets";
import { ImageGridEditor, ImageGridView } from "../components/workbench/ImageGridEditor";
import { CoverEditor, CoverView, ContentsView } from "../components/workbench/CoverPage";
import { uploadFile, uploadUrl } from "../lib/uploads";
import DictTextarea from "../components/DictTextarea";

// Iter 290zp.12 (Stage B) — Workbench attachment-split feature flag.
// When ON (default in preview), the Report Builder shows the new
// "Proposal-only attachments" uploader (files that go in the PDF but
// not in the chat). When OFF, the legacy chat-only flow is the sole
// source of report attachments. Lets us roll back the split UI by
// toggling a single env var without redeploying code.
const WORKBENCH_SPLIT_UI = (
  process.env.REACT_APP_WORKBENCH_SPLIT_UI ?? "1"
) !== "0";

/* ─────────────── Inline copy-to-clipboard helper ──────────────────── */
async function copyToClipboard(text, label = "Copied") {
  if (!text) return;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(label);
  } catch {
    toast.error("Couldn't copy — long-press the text to select manually.");
  }
}

function MessageCopyButton({ text, testId, className = "" }) {
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={() => copyToClipboard(text, "Message copied")}
      className={`text-[10px] text-[#647087] hover:text-[#1FB6C1] transition flex items-center gap-1 ${className}`}
      title="Copy message text"
      data-testid={testId}
      aria-label="Copy message"
    >
      <Copy className="w-3 h-3" />
      Copy
    </button>
  );
}

const STAGES = [
  { key: "brainstorm", label: "Brainstorm", icon: Sparkles, color: "#A78BFA" },
  { key: "plan", label: "Plan", icon: ClipboardList, color: "#1FB6C1" },
  { key: "estimate", label: "Estimate", icon: Calculator, color: "#F59E0B" },
];

/* ─────────────── Stage progress strip (inline, no modal) ──────────── */
function StageStrip({ draft, onPromote, onBack, busy }) {
  const stageIdx = STAGES.findIndex((s) => s.key === draft.current_stage);
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 sm:p-4" data-testid="workbench-stage-strip">
      <div className="flex items-center gap-2 sm:gap-4">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const reached = i <= stageIdx;
          const active = i === stageIdx;
          return (
            <div key={s.key} className="flex items-center gap-2 flex-1 min-w-0" data-testid={`stage-${s.key}`}>
              <div
                className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 transition ${
                  active ? "ring-2 ring-cyan-400/60" : ""
                }`}
                style={{
                  background: reached ? `${s.color}22` : "rgba(255,255,255,0.04)",
                  color: reached ? s.color : "#647087",
                }}
              >
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className={`text-xs font-medium truncate ${active ? "text-white" : reached ? "text-[#cbd5e1]" : "text-[#647087]"}`}>
                  {s.label}
                </div>
              </div>
              {i < STAGES.length - 1 && (
                <ArrowRight className="w-3 h-3 text-[#647087] shrink-0 hidden sm:block" />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="workbench-stage-actions">
        {draft.current_stage === "brainstorm" && draft.status === "active" && (
          <button
            type="button"
            onClick={() => onPromote("plan")}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="promote-plan-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
            Promote to plan
          </button>
        )}
        {draft.current_stage === "plan" && draft.status === "active" && (
          <>
            <button
              type="button"
              onClick={() => onPromote("estimate")}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="promote-estimate-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />}
              Promote to estimate
            </button>
            <button
              type="button"
              onClick={() => onBack("brainstorm")}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="back-brainstorm-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Back to brainstorm
            </button>
          </>
        )}
        {draft.current_stage === "estimate" && draft.status === "active" && (
          <>
            <button
              type="button"
              onClick={() => onBack("plan")}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="back-plan-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Back to plan
            </button>
            <button
              type="button"
              onClick={() => onBack("brainstorm")}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="back-brainstorm-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Back to brainstorm
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────── Brainstorm chat — reuses platform ChatComposer +
   ChatMessage so we get voice + transcription + multi-file + paste +
   the per-message copy button automatically.

   Iter 239 — generalised to `StageChat`: drives brainstorm, plan and
   estimate. Stage controls (a) the empty-state copy, (b) the header
   label, (c) whether to use fast_mode (brainstorm only — plan and
   estimate need full reasoning depth). */

// Iter 427.p17 (Dirk 2026-09-06): "I want my reports back instantly
// the way it was BEFORE consolidation." Consolidation flow removed.
// The Consolidate button is gone; the report generator now runs
// straight off the brainstorm transcript again (recency-weighted
// primacy taught in the system prompt).

const STAGE_META = {
  brainstorm: {
    label: "Brainstorm with Hidro",
    accent: "text-purple-300",
    empty:
      "Tell Hidro what system you're thinking about. Pipe sizes, pump heads, " +
      "filter stages — sketch it in plain English (or tap the mic to dictate) " +
      "and the AI will help you refine. When you're ready, hit Promote to plan above.",
    fastMode: true,
    historyDepth: 12,
  },
  plan: {
    label: "Planning with Hidro",
    accent: "text-cyan-300",
    empty:
      "Drop the first phase you'd like to plan — pumps, pipework, valves, " +
      "instrumentation. List parts (qty × size/material) and Hidro will spot " +
      "the gaps (clips, brackets, fittings).",
    fastMode: false,
    historyDepth: 40,
  },
  estimate: {
    label: "Estimating with Hidro",
    accent: "text-amber-300",
    empty:
      "Walk Hidro through the costs you're considering — labour rates, travel, " +
      "supplier prices. When the numbers feel round, click Build estimate to " +
      "materialise priced line items.",
    fastMode: false,
    historyDepth: 40,
  },
};

function StageChat({ sessionId, customerId, stage = "brainstorm", draftId, kind, initialPrompt }) {
  const [prefill, setPrefill] = useState(initialPrompt ? { text: initialPrompt, n: 1 } : null);
  // Preset prompt is one-shot: drop it from history so a refresh doesn't re-insert it.
  useEffect(() => {
    if (initialPrompt) window.history.replaceState({ ...(window.history.state || {}), usr: undefined }, "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const meta = STAGE_META[stage] || STAGE_META.brainstorm;
  const [messages, setMessages] = useState([]);
  const [asking, setAsking] = useState(false);
  const askingRef = useRef(false);
  const pollAbortRef = useRef(false);
  const scroller = useRef(null);

  // Iter 244 — All poll calls go via `_pollGet` which appends a
  // cache-bust query param (`?_=<ts>`) AND a `Cache-Control: no-cache`
  // header. Belt-and-braces against both the service-worker cache and
  // the browser HTTP cache — the PWA's stalest enemy. Single source of
  // truth so we can't accidentally introduce a cached poll later.
  const _pollGet = useCallback((path) => api.get(path, {
    params: { _: Date.now() },
    headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
  }), []);

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await _pollGet(`/engineering/sessions/${sessionId}/messages`);
      // Server returns `{items: [...]}` — see `routes/engineering.list_messages`.
      const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      setMessages(list);
    } catch {
      setMessages([]);
    }
  }, [sessionId, _pollGet]);

  useEffect(() => { load(); }, [load]);

  // Iter 236.4 — re-engage polling if the user reloaded while an
  // assistant placeholder is still in `status: running` (e.g. closed
  // the tab mid-thinking, came back). Without this the bubble would
  // ghost-spin forever for the user.
  useEffect(() => {
    if (!sessionId) return;
    const placeholder = messages.find((m) => m.is_assistant && m.status === "running");
    if (placeholder && !askingRef.current) {
      askingRef.current = true;
      setAsking(true);
      pollAssistantMessage(placeholder.id).finally(() => {
        askingRef.current = false;
        setAsking(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, messages.length]);

  // Iter 236.5 — Continuous background message refresh (2 s cadence).
  // Mirrors the proven EngineeringSession.jsx safety net. The dedicated
  // single-message poll above can miss a tick (transient network blip,
  // race with the status flip on the server). Without an unconditional
  // top-up the assistant bubble can stay blank until the user reloads
  // the page — Dirk's recurring "blank response" complaint.
  // The shallow-equality bail (id+status+text on the last row) keeps
  // re-renders cheap so this doesn't thrash the message list.
  //
  // Iter 243 — Mobile reliability pass. On phones, when the screen
  // locks or the app loses focus (even for ~1 s), setInterval gets
  // aggressively throttled (Chrome → 1/minute, Safari → paused). When
  // the user returns to the app, the next tick can be a full minute
  // away — that's why the user has to "exit and re-enter" or hit the
  // hard sync to see new messages. Three reinforcements:
  //   (1) immediate refresh on `visibilitychange` (returning to app),
  //       `focus` (alt-tab back), and `online` (network restored)
  //   (2) interval keeps trying even while hidden (the throttled tick
  //       still beats no tick at all)
  //   (3) when autoRespond runs, a burst-poll fires every 1 s for the
  //       next 40 s so the reply lands fast (see autoRespond below)
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await _pollGet(`/engineering/sessions/${sessionId}/messages`);
        if (cancelled) return;
        const fresh = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setMessages((prev) => {
          // Iter 427.p14 — voice-race companion: the shallow-equality
          // bail below MUST detect transcription state changes.
          // Previously we only compared length + last row's id/status/
          // text, so a Whisper transcription landing on an existing
          // audio bubble was silently discarded and only appeared
          // after a manual "Sync" refresh. Now we also key on the
          // sum of `transcription_status` across audio rows so any
          // pending→done flip forces a re-render.
          const prevAudioSig = prev
            .filter((m) => m.kind === "audio")
            .map((m) => `${m.id}:${m.transcription_status || "-"}`)
            .join("|");
          const freshAudioSig = fresh
            .filter((m) => m.kind === "audio")
            .map((m) => `${m.id}:${m.transcription_status || "-"}`)
            .join("|");
          if (
            prev.length === fresh.length
            && prev[prev.length - 1]?.id === fresh[fresh.length - 1]?.id
            && prev[prev.length - 1]?.status === fresh[fresh.length - 1]?.status
            && prev[prev.length - 1]?.text === fresh[fresh.length - 1]?.text
            && prevAudioSig === freshAudioSig
          ) {
            return prev;
          }
          return fresh;
        });
      } catch { /* keep polling on transient errors */ }
    };
    const handle = setInterval(tick, 2000);
    // Iter 243 — wake-up listeners. Any of these events suggests the
    // user just came back to the app, so fetch fresh state right now.
    const onWake = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
    // Also fire one tick immediately on mount so the initial paint
    // catches any messages the load() call missed.
    tick();
    return () => {
      cancelled = true;
      clearInterval(handle);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
    };
  }, [sessionId, _pollGet]);

  // Iter 427.p14 — Boost the poll cadence while any voice note is
  // still transcribing so the transcription lands under the audio
  // bubble within ~1 s of Whisper finishing. Without this the
  // background 2 s tick was already catching it (once the shallow-
  // equality bail was fixed above), but on mobile the tick can get
  // throttled to 60 s in the background — this dedicated 1.2 s
  // interval fires ONLY while there's pending audio, so it costs
  // nothing when the chat is idle.
  useEffect(() => {
    if (!sessionId) return undefined;
    const anyPending = (messages || []).some(
      (m) => m.kind === "audio" && m.transcription_status === "pending",
    );
    if (!anyPending) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await _pollGet(`/engineering/sessions/${sessionId}/messages`);
        if (cancelled) return;
        const fresh = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setMessages(fresh);
      } catch { /* keep polling */ }
    };
    const handle = setInterval(tick, 1200);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [sessionId, messages, _pollGet]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, asking]);

  // Iter 290zp.7b — Map appendix message-id → ordinal index so the
  // read-only chip under each bubble can show "Appendix · #2" without
  // every chip having to scan the full message list.
  const appendixIndexById = useMemo(() => {
    const map = {};
    (messages || [])
      .filter((m) => m.report_inclusion === "appendix" && m.upload_id)
      .slice()
      .sort((a, b) => (a.report_order ?? 0) - (b.report_order ?? 0))
      .forEach((m, i) => { map[m.id] = i; });
    return map;
  }, [messages]);

  // Poll the placeholder assistant message via the single-message
  // endpoint (which runs the >180s sweeper) until the worker writes a
  // terminal status. Same pattern as EngineeringSession.
  const pollAssistantMessage = async (mid) => {
    pollAbortRef.current = false;
    const maxAttempts = 60; // ~120s at 2s/poll — Haiku replies in ~10s
    for (let i = 0; i < maxAttempts; i += 1) {
      if (pollAbortRef.current) return;
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data: m } = await _pollGet(`/engineering/sessions/${sessionId}/messages/${mid}`);
        if (m && m.status !== "running") {
          setMessages((prev) => prev.map((x) => (x.id === mid ? m : x)));
          return;
        }
      } catch {
        // keep polling
      }
    }
    // Loop ran out — surface a terminal error so the bubble doesn't
    // ghost-spin (the server sweeper has likely already flipped it,
    // but be defensive).
    setMessages((prev) => prev.map((x) => (
      x.id === mid && x.status === "running"
        ? { ...x, status: "error", text: "Hidro is taking longer than expected. Refresh in a moment — the answer may already be saved." }
        : x
    )));
  };

  // Iter 243 — Burst-poll the messages list at 1 s cadence for ~40 s
  // after every send. This is the belt-and-braces companion to the
  // single-message `pollAssistantMessage` and the 2-s background loop.
  // Together they make sure the FE sees the assistant reply within
  // 1-2 seconds of the server flipping it to `ready`, even on a
  // throttled mobile WebView. Idempotent: kicked from autoRespond.
  const burstPollRef = useRef(null);
  const startBurstPoll = useCallback(() => {
    if (!sessionId) return;
    // Cancel any in-flight burst first.
    if (burstPollRef.current) {
      clearInterval(burstPollRef.current.handle);
      burstPollRef.current.cancelled = true;
    }
    const state = { cancelled: false, ticks: 0, handle: null };
    burstPollRef.current = state;
    const tick = async () => {
      if (state.cancelled) return;
      state.ticks += 1;
      try {
        const { data } = await _pollGet(`/engineering/sessions/${sessionId}/messages`);
        if (state.cancelled) return;
        const fresh = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setMessages((prev) => {
          // Iter 427.p14 — audio transcription signature so pending→done
          // flips force a re-render even when the tail message is unchanged.
          const prevAudioSig = prev
            .filter((m) => m.kind === "audio")
            .map((m) => `${m.id}:${m.transcription_status || "-"}`)
            .join("|");
          const freshAudioSig = fresh
            .filter((m) => m.kind === "audio")
            .map((m) => `${m.id}:${m.transcription_status || "-"}`)
            .join("|");
          if (
            prev.length === fresh.length
            && prev[prev.length - 1]?.id === fresh[fresh.length - 1]?.id
            && prev[prev.length - 1]?.status === fresh[fresh.length - 1]?.status
            && prev[prev.length - 1]?.text === fresh[fresh.length - 1]?.text
            && prevAudioSig === freshAudioSig
          ) {
            return prev;
          }
          return fresh;
        });
        // Stop once we have a non-running last assistant message.
        const last = fresh[fresh.length - 1];
        if (last?.is_assistant && last?.status && last.status !== "running") {
          state.cancelled = true;
          clearInterval(state.handle);
        }
      } catch { /* swallow */ }
      if (state.ticks >= 40 && !state.cancelled) {
        state.cancelled = true;
        clearInterval(state.handle);
      }
    };
    state.handle = setInterval(tick, 1000);
    tick(); // immediate first tick
  }, [sessionId, _pollGet]);
  useEffect(() => () => {
    if (burstPollRef.current) {
      burstPollRef.current.cancelled = true;
      clearInterval(burstPollRef.current.handle);
    }
  }, []);

  const autoRespond = async (sentMsg) => {
    if (!sentMsg || sentMsg.is_assistant || sentMsg.is_datasheet_ref) return;
    if (askingRef.current) return;
    askingRef.current = true;
    setAsking(true);
    // Kick the burst-poll BEFORE we await /ask. The user's own message
    // is already saved server-side (ChatComposer just POSTed it), so
    // even if the /ask call is slow, the burst poll guarantees the
    // user-bubble shows immediately and the assistant placeholder
    // appears the moment the server creates it.
    startBurstPoll();
    try {
      const { data } = await api.post(
        `/engineering/sessions/${sessionId}/ask`,
        {
          user_message_id: sentMsg.id,
          // Iter 239 — stage-aware. Brainstorm = fast_mode (snappy),
          // plan/estimate = full reasoning depth.
          fast_mode: meta.fastMode,
          deep_mode: !meta.fastMode,
          include_history: meta.historyDepth,
        },
        // Allow more wall-clock for plan/estimate (real engineering
        // reasoning takes longer than a brainstorm one-liner).
        { timeout: meta.fastMode ? 30_000 : 90_000 },
      );
      setMessages((prev) => {
        // Idempotent merge — burst poll may have already inserted
        // the placeholder from the list endpoint.
        if (prev.some((x) => x.id === data.id)) return prev;
        return [...prev, data];
      });
      await pollAssistantMessage(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hidro couldn't reply right now.");
    } finally {
      askingRef.current = false;
      setAsking(false);
    }
  };

  // Cancel polling if the component unmounts mid-flight.
  useEffect(() => () => { pollAbortRef.current = true; }, []);

  const handleSent = (m) => {
    setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
  };

  return (
    <div
      className="rounded-lg border border-white/10 bg-white/5 flex flex-col"
      data-testid={`workbench-stage-chat-${stage}`}
      style={{ height: "60vh", minHeight: "420px" }}
    >
      <header className="px-3 py-2 border-b border-white/5 flex items-center gap-2 text-xs text-[#a0aabf]">
        <Sparkles className={`w-3.5 h-3.5 ${meta.accent}`} />
        {meta.label} · multi-turn memory for this stage
        {customerId && <span className="ml-auto text-[10px]">customer linked</span>}
      </header>
      <div ref={scroller} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid={`workbench-stage-messages-${stage}`}>
        {messages.length === 0 && (
          <p className="text-xs text-[#647087] text-center mt-12 max-w-md mx-auto">
            {meta.empty}
          </p>
        )}
        {messages.map((m) => {
          const isAssistant = !!m.is_assistant;
          if (isAssistant) {
            // Assistant bubble: render markdown + inline copy button.
            const txt = m.text || "";
            const isError = m.status === "error";
            const isRunning = m.status === "running" || (!m.status && !txt);
            return (
              <div key={m.id} className="group relative flex justify-start" data-testid={`workbench-msg-${m.id}`}>
                <div className={`max-w-[88%] rounded-lg px-3 py-2 text-xs border ${
                  isError
                    ? "bg-rose-500/10 border-rose-400/30 text-rose-100"
                    : m.stage_seed
                      ? "bg-[#0d1b22] border-cyan-400/40 text-[#e5f1f5] ring-1 ring-cyan-400/20 shadow-[0_0_24px_-12px_rgba(34,211,238,0.4)]"
                      : "bg-white/5 border-white/10 text-[#e5e7eb]"
                }`}
                data-stage-seed={m.stage_seed ? "true" : undefined}
                >
                  {m.stage_seed && (
                    <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300 mb-1.5 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Stage handoff briefing
                    </div>
                  )}
                  {isRunning ? (
                    <span className="inline-flex items-center gap-2 text-[#a0aabf]">
                      <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.post(`/engineering/sessions/${sessionId}/messages/${m.id}/cancel`, {});
                            toast.success("Cancelled. Hidro will stop replying.");
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Cancel failed.");
                          }
                        }}
                        className="ml-2 text-[10px] text-rose-300 hover:text-rose-200 underline"
                        data-testid={`workbench-msg-cancel-${m.id}`}
                        title="Stop Hidro mid-thought"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <HidroMarkdown text={txt} compact testId={`workbench-assistant-text-${m.id}`} />
                  )}
                  {txt && !isRunning && (
                    <div className="mt-1.5 opacity-0 group-hover:opacity-100 transition flex justify-end">
                      <MessageCopyButton text={txt} testId={`workbench-copy-assistant-${m.id}`} />
                    </div>
                  )}
                </div>
              </div>
            );
          }
          // User bubble: use the platform ChatMessage so audio / images /
          // files / etc. all render correctly with the per-message copy
          // button hovering below.
          return (
            <div key={m.id} className="group relative" data-testid={`workbench-msg-${m.id}`}>
              <ChatMessage msg={m} isMine isFirstInGroup members={[]} />
              {m.text && (
                <div className="flex justify-end pr-1 -mt-2 mb-2 opacity-0 group-hover:opacity-100 transition">
                  <MessageCopyButton text={m.text} testId={`workbench-copy-user-${m.id}`} />
                </div>
              )}
              {/* Iter 290zp.7b — read-only status chip; the controls
                  themselves live in the centralised attachments panel
                  above the composer so the user never has to scroll
                  through 50 messages to find an attachment. */}
              <ReportInclusionChip
                msg={m}
                appendixIndex={appendixIndexById[m.id]}
              />
            </div>
          );
        })}
      </div>
      {/* Iter 290zp.7b — centralised proposal-attachments panel
          (every image/PDF in this session, grouped by inclusion tier,
          with drag-to-reorder for the appendix list). */}
      <ReportAttachmentsPanel
        sessionId={sessionId}
        messages={messages}
        onChanged={(updated) => {
          if (updated && updated.id) {
            // Optimistic local merge so the UI updates instantly
            // instead of waiting for the next 2-s poll.
            setMessages((prev) => prev.map((x) => (
              x.id === updated.id ? { ...x, ...updated } : x
            )));
          } else {
            load();
          }
        }}
      />
      <div className="border-t border-white/5 p-2">
        {draftId && (
          <div className="px-1 pb-1 flex items-center gap-2">
            <PresetInsertButton draftId={draftId} kind={kind} onInsert={(t) => setPrefill((p) => ({ text: t, n: (p?.n || 0) + 1 }))} />
          </div>
        )}
        <ChatComposer
          threadId={sessionId}
          apiBase="/engineering/sessions"
          onSent={handleSent}
          onBatchDone={autoRespond}
          prefill={prefill}
          disabled={asking}
        />
      </div>
    </div>
  );
}

/* ─────────────── Plan summary panel ──────────────────────────────── */
function PlanPanel({ draft }) {
  const plan = draft.plan;
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 sm:p-4 space-y-3" data-testid="workbench-plan-panel">
      <header className="flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-cyan-300" />
        <h2 className="text-sm font-semibold">System plan</h2>
      </header>
      {plan ? (
        <>
          <p className="text-xs text-[#a0aabf] whitespace-pre-wrap">
            {plan.description || "Plan is created — open the planner to add components."}
          </p>
          {!!plan.items?.length && (
            <div className="text-[11px] text-[#647087]">
              {plan.items.length} component{plan.items.length === 1 ? "" : "s"} mapped
            </div>
          )}
          {draft.project_id ? (
            <Link
              to={`/projects/${draft.project_id}?tab=planner`}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="workbench-open-planner"
            >
              Open in Planner <ExternalLink className="w-3 h-3" />
            </Link>
          ) : (
            <p className="text-[11px] text-amber-300">
              Promote this draft to a real project to open the full Planner.
            </p>
          )}
        </>
      ) : (
        <p className="text-xs text-[#647087]">No plan yet — promote your brainstorm to get started.</p>
      )}
    </div>
  );
}

/* ─────────────── Estimate summary panel ──────────────────────────── */
function EstimatePanel({ draft }) {
  const e = draft.estimate;
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 sm:p-4 space-y-3" data-testid="workbench-estimate-panel">
      <header className="flex items-center gap-2">
        <Calculator className="w-4 h-4 text-amber-300" />
        <h2 className="text-sm font-semibold">Estimate</h2>
      </header>
      {e ? (
        <>
          <div className="text-xs text-[#a0aabf]">
            {e.title || "Estimate draft"} · {e.line_items?.length || 0} line item{e.line_items?.length === 1 ? "" : "s"}
          </div>
          <Link
            to={`/estimator/draft/${e.id}`}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="workbench-open-estimator"
          >
            Open in Estimator <ExternalLink className="w-3 h-3" />
          </Link>
        </>
      ) : (
        <p className="text-xs text-[#647087]">No estimate yet — promote the plan to get a priced draft.</p>
      )}
    </div>
  );
}

/* ─────────────── Promote-to-project inline panel ─────────────────── */
function PromoteToProjectPanel({ draft, onPromoted }) {
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState(draft.customer_id || "");
  const [customers, setCustomers] = useState([]);
  const [title, setTitle] = useState(draft.name || "");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/customers?limit=500").then(({ data }) => {
      setCustomers(Array.isArray(data) ? data : []);
    }).catch(() => setCustomers([]));
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return customers.slice(0, 50);
    return customers.filter((c) => (c.name || "").toLowerCase().includes(s)).slice(0, 50);
  }, [customers, search]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!customerId) {
      toast.error("Pick a customer first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/promote/project`,
        { customer_id: customerId, project_title: title.trim() || undefined },
      );
      toast.success("Promoted to real project.");
      onPromoted?.(data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Promote failed.");
    } finally {
      setBusy(false);
    }
  };

  if (draft.status === "promoted") {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-200 flex items-center gap-2" data-testid="workbench-promoted-banner">
        <CheckCircle2 className="w-4 h-4" />
        Promoted to <Link to={`/projects/${draft.project_id}`} className="underline">project</Link>{" "}
        — all future work happens there.
      </div>
    );
  }

  return open ? (
    <form onSubmit={submit} className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 p-3 space-y-2" data-testid="workbench-promote-form">
      <header className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] text-cyan-200 font-bold flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> Promote to a real project
        </span>
        <button type="button" onClick={() => setOpen(false)} className="text-[10px] text-[#647087] hover:text-white">
          Cancel
        </button>
      </header>
      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Project title</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={draft.name}
          className="hv-input mt-1 text-xs"
          data-testid="workbench-promote-title"
        />
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Customer</span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customers…"
          className="hv-input mt-1 text-xs"
          data-testid="workbench-promote-search"
        />
        <ul className="max-h-40 overflow-y-auto mt-1 space-y-1" data-testid="workbench-promote-customers">
          {filtered.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => setCustomerId(c.id)}
                className={`w-full text-left px-2 py-1 rounded text-xs ${
                  customerId === c.id
                    ? "bg-cyan-500/15 border border-cyan-400/40 text-cyan-100"
                    : "border border-white/10 hover:border-white/25 text-[#cbd5e1]"
                }`}
                data-testid={`workbench-promote-cust-${c.id}`}
              >
                {c.name}
              </button>
            </li>
          ))}
          {filtered.length === 0 && <li className="text-[10px] text-[#647087] px-2 py-1">No customers match.</li>}
        </ul>
      </label>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy || !customerId}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="workbench-promote-submit"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Promote to project
        </button>
      </div>
    </form>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="hv-btn hv-btn-ghost text-xs"
      data-testid="workbench-promote-open"
    >
      <Users className="w-3.5 h-3.5" /> Promote to a real project…
    </button>
  );
}

/* ─────────────── Link-to-existing-project inline panel (Iter 424.c) ── */
function LinkToProjectPanel({ draft, onLinked }) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/projects?limit=500&status=all").then(({ data }) => {
      const rows = Array.isArray(data) ? data : (data?.items || []);
      // Hide archived / completed by default — operators almost always
      // want to link a fresh brainstorm to an in-flight project.
      setProjects(rows.filter((p) => !p.archived_at && p.status !== "closed"));
    }).catch(() => setProjects([]));
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return projects.slice(0, 50);
    return projects
      .filter((p) => (p.title || "").toLowerCase().includes(s)
        || (p.short_id || "").toLowerCase().includes(s))
      .slice(0, 50);
  }, [projects, search]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!projectId) { toast.error("Pick a project first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/link/project`,
        { project_id: projectId },
      );
      toast.success("Linked to project.");
      onLinked?.(data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Link failed.");
    } finally { setBusy(false); }
  };

  if (draft.status === "promoted") return null;

  return open ? (
    <form onSubmit={submit} className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="workbench-link-form">
      <header className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] text-amber-200 font-bold flex items-center gap-2">
          <ExternalLink className="w-3.5 h-3.5" /> Link to an existing project
        </span>
        <button type="button" onClick={() => setOpen(false)} className="text-[10px] text-[#647087] hover:text-white" data-testid="workbench-link-cancel">
          Cancel
        </button>
      </header>
      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Project</span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title or P-code…"
          className="hv-input mt-1 text-xs"
          data-testid="workbench-link-search"
        />
        <ul className="max-h-40 overflow-y-auto mt-1 space-y-1" data-testid="workbench-link-projects">
          {filtered.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setProjectId(p.id)}
                className={`w-full text-left px-2 py-1 rounded text-xs ${
                  projectId === p.id
                    ? "bg-amber-500/15 border border-amber-400/40 text-amber-100"
                    : "border border-white/10 hover:border-white/25 text-[#cbd5e1]"
                }`}
                data-testid={`workbench-link-proj-${p.id}`}
              >
                <span className="font-mono text-[10px] text-[#647087] mr-2">{p.short_id}</span>
                {p.title || "Untitled project"}
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="text-[10px] text-[#647087] px-2 py-1">No projects match.</li>
          )}
        </ul>
      </label>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy || !projectId}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="workbench-link-submit"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
          Link to project
        </button>
      </div>
    </form>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="hv-btn hv-btn-ghost text-xs"
      data-testid="workbench-link-open"
    >
      <ExternalLink className="w-3.5 h-3.5" /> Link to an existing project…
    </button>
  );
}

/* ─────────────── Detail page ──────────────────────────────────────── */

/* ─────────────── Reports panel — generate / rename / PDF / send ──── */
const AUDIENCE_OPTIONS = [
  {
    key: "customer",
    label: "Customer",
    hint: "Plain language · what's wrong, why & how we'll fix it",
  },
  {
    key: "technician",
    label: "Technician",
    hint: "Steps · installation · commissioning · on-site",
  },
  {
    key: "engineer",
    label: "Engineering",
    hint: "Math · standards · tolerances · BOM outline",
  },
];

const LENGTH_OPTIONS = [
  { key: "short",       label: "Short",       hint: "≤ 300 words · exec brief" },
  { key: "medium",      label: "Medium",      hint: "600–900 words · structured" },
  { key: "long",        label: "Long",        hint: "1200–1800 words · full brief" },
  { key: "extra_long",  label: "Extra long",  hint: "No word limit · every stage in depth" },
  { key: "verbatim",    label: "Use existing", hint: "Brainstorm synthesis byte-for-byte · no LLM re-write" },
];

// Iter 427.p22 — Advanced Report Section Builder.
// Adds to the p21 MVP:
//   1. Mic per section refine input (reuses VoiceDictation, which
//      awaits Whisper before onText — p14 voice-race guarantee).
//   2. Section reorder / add / delete (deterministic, no LLM).
//   3. Delta-instruction at the report level ("remove sections 1 and
//      2") — LLM parses to a JSON ops list, FE previews, user
//      confirms, deterministic apply. LLM never rewrites bodies.
//   4. Read | Edit | Split three-mode view. Split shows the compiled
//      HTML on the left and the section cards on the right (wide
//      screens only; falls back to Edit on narrow viewports).
//   5. Section-kind density defaults baked into the refine prompt on
//      the backend — no FE work beyond showing the kind badge.
// Editable document title — the H1 the PDF prints above section 1.
function ReportTitleEditor({ report, did, onChanged }) {
  const [title, setTitle] = React.useState(report.headline || "");
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setTitle(report.headline || ""); }, [report.headline]);
  const save = async () => {
    const next = title.trim();
    if (next === (report.headline || "").trim()) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/workbench/drafts/${did}/reports/${report.id}`, { headline: next });
      onChanged?.(data);
      toast.success("Title saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save title.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-md border border-cyan-500/25 bg-cyan-500/[0.04] p-2.5" data-testid={`workbench-report-title-card-${report.id}`}>
      <div className="text-[10px] uppercase tracking-wider text-cyan-200/80 mb-1">Document title</div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        disabled={saving}
        placeholder="Report title (prints as the PDF heading)"
        className="w-full bg-transparent border-b border-white/15 focus:border-cyan-400 outline-none text-sm font-semibold text-cyan-50 py-1"
        data-testid={`workbench-report-title-input-${report.id}`}
      />
    </div>
  );
}

function ReportSectionTabs({ report, did, onChanged, isRevising }) {
  const [tab, setTab] = React.useState("read");
  const [sections, setSections] = React.useState(report.sections || null);
  const [busySec, setBusySec] = React.useState(null);
  const [instr, setInstr] = React.useState({});
  // Track voice busy state per section so the Refine button waits
  // for transcription (p14 voice-race guarantee).
  const [voiceBusy, setVoiceBusy] = React.useState({});
  // Report-level delta instruction: input, plan preview, apply state.
  const [deltaText, setDeltaText] = React.useState("");
  const [deltaVoiceBusy, setDeltaVoiceBusy] = React.useState(false);
  const [deltaPlan, setDeltaPlan] = React.useState(null);
  const [deltaPlanning, setDeltaPlanning] = React.useState(false);
  const [deltaApplying, setDeltaApplying] = React.useState(false);
  // Iter 427.p22b — bounded delta-op history (server keeps 5). Each entry:
  // { id, at, by, instruction, ops }. Newest first. Populated by
  // apply-response and lazy-fetched when the Edit tab opens.
  const [opHistory, setOpHistory] = React.useState([]);
  const [undoingOp, setUndoingOp] = React.useState(null);
  const rid = report.id;

  // Iter 427.p22 — sync FROM parent only when the incoming section
  // ids/count actually differ from what we have locally. Without this
  // guard the round-trip is:
  //   child mutation → _bubble → parent setReports → new report ref
  //   → this effect fires → setSections → child re-renders → effect
  //   fires again → "Maximum update depth exceeded".
  // A shallow id-list compare is enough: we don't need deep equality
  // because the only field that identifies section-array shape is the
  // ordered list of ids. Body/heading edits inside a section don't
  // matter here — those are already the child's authoritative copy.
  React.useEffect(() => {
    const incoming = report.sections;
    if (!incoming) return;
    const cur = sections || [];
    if (cur.length === incoming.length) {
      let same = true;
      for (let i = 0; i < cur.length; i++) {
        if (cur[i]?.id !== incoming[i]?.id) { same = false; break; }
      }
      if (same) return;
    }
    setSections(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, report.sections]);

  const loadSections = async () => {
    try {
      const { data } = await api.get(`/workbench/drafts/${did}/reports/${rid}/sections`);
      setSections(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load sections.");
    }
  };

  React.useEffect(() => {
    if ((tab === "edit" || tab === "split") && !sections) loadSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Iter 427.p22b — lazy-load the delta-op history when Edit / Split
  // is first opened. It's kept server-side (last 5 entries).
  const loadOpHistory = async () => {
    try {
      const { data } = await api.get(
        `/workbench/drafts/${did}/reports/${rid}/section-ops/history`,
      );
      setOpHistory(data?.items || []);
    } catch {
      /* silent — not critical */
    }
  };
  React.useEffect(() => {
    if (tab === "edit" || tab === "split") loadOpHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, rid]);

  // Iter 427.p22 — bubble a proper payload up to ReportsPanel so the
  // cached report row keeps its `sections` + `body` in sync. Calling
  // onChanged() with no args used to crash the parent (undefined.id).
  const _bubble = (payload) => {
    if (!payload) return onChanged?.({ id: rid });
    onChanged?.({ id: rid, ...payload });
  };

  const patchSection = async (secId, updates) => {
    try {
      const { data } = await api.patch(
        `/workbench/drafts/${did}/reports/${rid}/sections/${secId}`, updates,
      );
      setSections(data?.sections || []);
      _bubble({ sections: data?.sections, body: data?.body });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const refineSection = async (secId, opts = {}) => {
    const text = (instr[secId] || "").trim();
    if (!text) return;
    // p14 voice-race guarantee — if the mic is still recording or
    // transcribing for this section, wait for onText to land the
    // transcript before firing the refine.
    if (voiceBusy[secId]) {
      toast.message("Wait for the transcript to finish…");
      return;
    }
    setBusySec(secId);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${did}/reports/${rid}/sections/${secId}/refine`,
        {
          instruction: text,
          // Iter 427.p57.h — Brainstorm-context toggle. Default off so
          // the fast refine stays fast; per-section opt-in via the
          // "Use brainstorm context" checkbox below the input.
          use_brainstorm_context: !!opts.useBrainstormCtx,
        },
      );
      setSections(data?.sections || []);
      setInstr((prev) => ({ ...prev, [secId]: "" }));
      toast.success(
        data?.context_used
          ? "Section refined with brainstorm context."
          : "Section refined.",
      );
      _bubble({ sections: data?.sections, body: data?.body });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refine failed.");
    } finally {
      setBusySec(null);
    }
  };

  const addSection = async (atIndex, opts = {}) => {
    try {
      const payload = {
        heading: opts.kind === "image_grid" || opts.kind === "cover" ? "" : (opts.heading || (opts.kind === "contents" ? "Contents" : "New section")),
        body: "",
        index: atIndex,
        ...(opts.kind ? { kind: opts.kind } : {}),
      };
      // Iter 427.p57.h — Optional brainstorm-seeded body. When
      // `seed_instruction` is supplied the backend fills in the body
      // by grounding an LLM call in the draft's full brainstorm
      // transcript + attachments. Other sections stay byte-identical.
      if (opts.seedInstruction) {
        payload.seed_instruction = opts.seedInstruction;
      }
      const { data } = await api.post(
        `/workbench/drafts/${did}/reports/${rid}/sections`,
        payload,
      );
      setSections(data?.sections || []);
      _bubble({ sections: data?.sections, body: data?.body });
      toast.success(
        data?.seeded
          ? "Section seeded from brainstorm."
          : "Section added.",
      );
      return data?.section || null;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed.");
      return null;
    }
  };

  // Iter 427.p50 — Add an image section. Uploads via the shared
  // /api/uploads flow (same helper JC photos / gallery / portal use),
  // then inserts a `kind: "image"` section with the returned
  // upload_id. `atIndex` = null → append at end.
  const addImageSection = async (file, atIndex, options = {}) => {
    if (!file) return;
    try {
      const up = await uploadFile(file, {
        target_kind: "workbench_report",
        target_id: rid,
        label: file.name,
      });
      const payload = {
        kind: "image",
        heading: "",
        body: "",
        upload_id: up.id,
        width_pct: options.width_pct ?? 60,
        align: "center",
        caption: "",
        index: atIndex,
      };
      // Iter 427.p57.e — Optional row_group so callers can drop a
      // new image DIRECTLY into an existing row without a second
      // PATCH round trip. Used by "Add image beside" on the inline
      // dropdown; standard "Add image below" leaves this null.
      if (options.row_group) payload.row_group = options.row_group;
      if (options.layout) payload.layout = options.layout;
      const { data } = await api.post(
        `/workbench/drafts/${did}/reports/${rid}/sections`,
        payload,
      );
      setSections(data?.sections || []);
      _bubble({ sections: data?.sections, body: data?.body });
      toast.success(options.row_group ? "Image added beside." : "Image added.");
      return data?.sections?.find((s) => s.upload_id === up.id) || null;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image add failed.");
      return null;
    }
  };

  // Iter 427.p50 — Import a PDF. Server rasterises each page and
  // inserts one image section per page (max 40 pages).
  const importPdfSections = async (file, insertAfter) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (insertAfter) fd.append("insert_after", insertAfter);
    const t = toast.loading(`Importing ${file.name} — rasterising pages…`);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${did}/reports/${rid}/sections/import-pdf`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setSections(data?.sections || []);
      _bubble({ sections: data?.sections, body: data?.body });
      toast.success(`Imported ${data?.inserted_count || 0} page(s).`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF import failed.", { id: t });
    }
  };

  const deleteSection = async (secId) => {
    if (!window.confirm("Delete this section? The rest of the report stays untouched.")) return;
    try {
      const { data } = await api.delete(
        `/workbench/drafts/${did}/reports/${rid}/sections/${secId}`,
      );
      setSections(data?.sections || []);
      _bubble({ sections: data?.sections, body: data?.body });
      toast.success("Section removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  // Iter 427.p51 — Drag-reorder via dnd-kit.
  //
  // Optimistic-update + rollback contract: apply the new order to
  // state immediately, PATCH /sections-order, reconcile against the
  // server's echo. On error we reload from the server so the FE never
  // drifts out of sync with Mongo.
  const onSectionDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!sections) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const nextOrder = arrayMove(sections, oldIdx, newIdx);
    setSections(nextOrder);
    try {
      const { data } = await api.patch(
        `/workbench/drafts/${did}/reports/${rid}/sections-order`,
        { order: nextOrder.map((s) => s.id) },
      );
      setSections(data?.sections || []);
      _bubble({ sections: data?.sections, body: data?.body });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed.");
      loadSections();
    }
  };

  // dnd-kit sensors — 5px activation distance stops accidental drags
  // when the user is clicking inside a textarea. Keyboard sensor gives
  // us free a11y (Tab-focus grip → Space to pick up → Arrows to move).
  const sectionDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const planDelta = async () => {
    const text = deltaText.trim();
    if (!text) return;
    if (deltaVoiceBusy) {
      toast.message("Wait for the transcript to finish…");
      return;
    }
    setDeltaPlanning(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${did}/reports/${rid}/section-ops/plan`,
        { instruction: text },
      );
      if (!data?.ops || data.ops.length === 0) {
        toast.message("Couldn't parse that into structural ops. Try 'remove section 3' or 'move commercial to the end'.");
        setDeltaPlan(null);
      } else {
        setDeltaPlan(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plan failed.");
    } finally {
      setDeltaPlanning(false);
    }
  };

  const applyDelta = async () => {
    if (!deltaPlan?.ops?.length) return;
    setDeltaApplying(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${did}/reports/${rid}/section-ops/apply`,
        { ops: deltaPlan.ops, instruction: deltaPlan.instruction || deltaText.trim() },
      );
      setSections(data?.sections || []);
      setOpHistory(data?.history || []);
      setDeltaPlan(null);
      setDeltaText("");
      _bubble({ sections: data?.sections, body: data?.body });
      toast.success("Applied. Undo is one tap away below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed.");
    } finally {
      setDeltaApplying(false);
    }
  };

  const undoOp = async (opId) => {
    setUndoingOp(opId);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${did}/reports/${rid}/section-ops/undo/${opId}`,
      );
      setSections(data?.sections || []);
      setOpHistory(data?.history || []);
      _bubble({ sections: data?.sections, body: data?.body });
      toast.success("Undone.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed.");
    } finally {
      setUndoingOp(null);
    }
  };

  const renderReadPane = () => (
    <div
      className="px-3 pb-3 pt-2 text-xs text-[#cbd5e1] max-h-[62vh] overflow-y-auto"
      data-testid={`workbench-report-draft-body-${rid}`}
    >
      {/* Iter 427.p50 — Render sections directly so image blocks
          render as real <figure>s instead of stripped HTML. Falls
          back to `report.body` for legacy reports with no sections
          array yet. */}
      {/* Document title (report.headline) — the PDF's H1. */}
      {(() => {
        const cover = (sections || []).find((s) => s.kind === "cover");
        const toc = (sections || []).find((s) => s.kind === "contents");
        return (
          <>
            {cover ? (
              <div className="mb-3"><CoverView sec={cover} /></div>
            ) : report.headline ? (
              <h1
                className="text-base font-semibold text-cyan-100 mb-2"
                data-testid={`workbench-report-read-title-${rid}`}
              >
                {report.headline}
              </h1>
            ) : null}
            {toc && <div className="mb-3"><ContentsView sec={toc} sections={sections} /><PageBreakMarker id={toc.id} /></div>}
          </>
        );
      })()}
      {sections && sections.length > 0 ? (
        <div className="space-y-3">
          {sections.map((s) => {
            if (s.kind === "cover" || s.kind === "contents") return null;
            if (s.kind === "image_grid") {
              return (
                <div key={s.id}>
                  {s.page_break_before && <PageBreakMarker id={s.id} />}
                  {s.heading && <h4 className="text-sm font-semibold mt-3">{s.heading}</h4>}
                  <ImageGridView sec={s} testId={`workbench-report-grid-${s.id}`} />
                </div>
              );
            }
            if (s.kind === "image" && s.upload_id) {
              const w = Math.max(10, Math.min(100, Number(s.width_pct || 60)));
              const align = ["left", "center", "right"].includes(s.align) ? s.align : "center";
              return (
                <figure
                  key={s.id}
                  style={{ textAlign: align, margin: "12px 0" }}
                  data-testid={`workbench-report-image-${s.id}`}
                >
                  {s.page_break_before && <PageBreakMarker id={s.id} />}
                  <img
                    src={uploadUrl(s.upload_id)}
                    alt={s.caption || "Report image"}
                    style={{ width: `${w}%`, height: "auto", maxWidth: "100%" }}
                    className="rounded"
                  />
                  {s.caption ? (
                    <figcaption className="text-[10px] text-[#647087] mt-1 italic">
                      {s.caption}
                    </figcaption>
                  ) : null}
                  {(s.vision_analysis_status || s.vision_analysis) && (
                    <div style={{ textAlign: "left" }}>
                      <VisionAnalysisChip msg={s} testIdPrefix="workbench-section-vision" />
                    </div>
                  )}
                </figure>
              );
            }
            const md = s.heading
              ? `## ${s.heading}\n\n${s.body || ""}`
              : (s.body || "");
            if (!md.trim()) return null;
            return (
              <div key={s.id}>
                {s.page_break_before && <PageBreakMarker id={s.id} />}
                <HidroMarkdown text={md} />
              </div>
            );
          })}
        </div>
      ) : (
        <HidroMarkdown text={report.body || ""} />
      )}
    </div>
  );

  const renderEditPane = () => (
    <div
      className="px-2.5 pb-3 pt-2 text-xs max-h-[62vh] overflow-y-auto space-y-2.5"
      data-testid={`workbench-report-edit-${rid}`}
    >
      {/* Delta instruction bar */}
      <div
        className="rounded-md border border-fuchsia-400/30 bg-fuchsia-500/[0.06] p-2 space-y-1.5"
        data-testid={`workbench-report-delta-panel-${rid}`}
      >
        <div className="text-[10px] uppercase tracking-wider text-fuchsia-200/80 flex items-center gap-1">
          <Wand2 className="w-3 h-3" />
          Report-level restructure (delta)
        </div>
        <div className="flex items-start gap-1.5">
          <input
            type="text"
            value={deltaText}
            onChange={(e) => setDeltaText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") planDelta(); }}
            placeholder="e.g. 'remove section 3', 'move commercial to the end', 'merge exclusions into scope'"
            disabled={deltaPlanning || deltaApplying}
            data-testid={`workbench-report-delta-input-${rid}`}
            className="flex-1 bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-fuchsia-400/40 border border-fuchsia-400/20"
          />
          <VoiceDictation
            disabled={deltaPlanning || deltaApplying}
            onStateChange={(s) => setDeltaVoiceBusy(!!s.busy)}
            onText={(t) => setDeltaText((prev) => (prev ? `${prev.trimEnd()} ${t}` : t))}
            testId={`workbench-report-delta-mic-${rid}`}
          />
          <button
            type="button"
            onClick={planDelta}
            disabled={deltaPlanning || deltaApplying || !deltaText.trim() || deltaVoiceBusy}
            data-testid={`workbench-report-delta-plan-btn-${rid}`}
            className="text-[10px] px-2 py-1.5 rounded border border-fuchsia-400/40 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-100 disabled:opacity-40 transition"
          >
            {deltaPlanning ? "Planning…" : "Preview"}
          </button>
        </div>
        {deltaPlan?.preview?.length > 0 && (
          <div
            className="rounded border border-fuchsia-400/40 bg-black/30 p-2 space-y-1.5"
            data-testid={`workbench-report-delta-preview-${rid}`}
          >
            <div className="text-[10px] text-fuchsia-100/80 font-medium">Planned changes:</div>
            <ul className="text-[11px] text-[#cbd5e1] space-y-0.5">
              {deltaPlan.preview.map((line, i) => (
                <li key={i} data-testid={`workbench-report-delta-preview-line-${i}`}>{line}</li>
              ))}
            </ul>
            <div className="flex gap-1.5 pt-1">
              <button
                type="button"
                onClick={applyDelta}
                disabled={deltaApplying}
                data-testid={`workbench-report-delta-apply-btn-${rid}`}
                className="text-[10px] px-2 py-1 rounded border border-emerald-400/50 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100 disabled:opacity-40 inline-flex items-center gap-1"
              >
                {deltaApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Apply {deltaPlan.ops.length} op{deltaPlan.ops.length === 1 ? "" : "s"}
              </button>
              <button
                type="button"
                onClick={() => setDeltaPlan(null)}
                disabled={deltaApplying}
                data-testid={`workbench-report-delta-cancel-btn-${rid}`}
                className="text-[10px] px-2 py-1 rounded border border-white/15 bg-black/20 hover:bg-black/40 text-[#a0aabf] inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Discard
              </button>
            </div>
          </div>
        )}
        {/* Iter 427.p22b — bounded delta-op history. Newest first, up
            to 5 entries. Each row is a one-tap undo that restores the
            snapshot the server took just before that op ran. */}
        {opHistory.length > 0 && (
          <div
            className="rounded border border-white/10 bg-black/20 p-2 space-y-1"
            data-testid={`workbench-report-delta-history-${rid}`}
          >
            <div className="text-[10px] text-[#a0aabf] font-medium flex items-center gap-1">
              <RotateCcw className="w-3 h-3" />
              Recent restructures (tap to undo — last {opHistory.length})
            </div>
            <ul className="space-y-1">
              {opHistory.map((h) => (
                <li
                  key={h.id}
                  className="flex items-start gap-2 text-[11px]"
                  data-testid={`workbench-report-delta-history-row-${h.id}`}
                >
                  <span className="flex-1 text-[#cbd5e1] truncate" title={h.instruction || `${(h.ops || []).length} ops`}>
                    <span className="text-[#647087] mr-1">
                      {new Date(h.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    "{(h.instruction || `${(h.ops || []).length} structural op(s)`).slice(0, 80)}"
                  </span>
                  <button
                    type="button"
                    onClick={() => undoOp(h.id)}
                    disabled={undoingOp === h.id}
                    data-testid={`workbench-report-delta-undo-btn-${h.id}`}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-400/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-100 disabled:opacity-40 inline-flex items-center gap-1 shrink-0"
                    title="Restore the section list to just before this op"
                  >
                    {undoingOp === h.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    Undo
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {!sections && (
        <div className="text-[#647087] text-[11px] px-1 py-4 text-center">
          Loading sections…
        </div>
      )}
      <ReportTitleEditor report={report} did={did} onChanged={onChanged} />
      {(sections || []).length > 0 && (
        <DndContext
          sensors={sectionDndSensors}
          collisionDetection={closestCenter}
          onDragEnd={onSectionDragEnd}
        >
          <SortableContext
            items={sections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {(sections || []).map((sec, idx) => (
              <SortableSectionCard
                key={sec.id}
                reportPath={`/workbench/drafts/${did}/reports/${rid}`}
                sec={sec}
                idx={idx}
                total={sections.length}
                sections={sections}
                addSection={addSection}
                addImageSection={addImageSection}
                importPdfSections={importPdfSections}
                deleteSection={deleteSection}
                patchSection={patchSection}
                refineSection={refineSection}
                setSections={setSections}
                instr={instr}
                setInstr={setInstr}
                busySec={busySec}
                voiceBusy={voiceBusy}
                setVoiceBusy={setVoiceBusy}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {/* Iter 427.p54 (Dirk 2026-02-20, Complaint 3) — Toolbar is
          ALWAYS visible when sections have loaded. Previously the
          Add-image + Import-PDF buttons were gated on
          `sections.length > 0`, so a fresh or just-refined report
          only saw a bare "Add blank section" button. That gate is
          gone: the same 3-button strip renders both empty and
          non-empty states. Empty-state helper line stays above the
          toolbar to explain why. */}
      {sections && (
        <>
          {sections.length === 0 && (
            <div className="text-[#647087] text-[11px] px-1 pt-3 pb-1 text-center italic">
              No sections yet — start by adding a section, an image, or importing a PDF.
            </div>
          )}
          <div className="grid grid-cols-3 gap-1.5" data-testid={`workbench-report-add-toolbar-${rid}`}>
            <button
              type="button"
              onClick={() => addSection(sections.length)}
              data-testid={`workbench-report-add-section-${rid}`}
              className="text-[10px] px-2 py-1.5 rounded border border-dashed border-cyan-400/30 bg-cyan-500/[0.03] hover:bg-cyan-500/10 text-cyan-100/80 inline-flex items-center justify-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add section
            </button>
            {/* Iter 427.p50 — Image + PDF import (labels match Dirk's spec). */}
            <label
              className="text-[10px] px-2 py-1.5 rounded border border-dashed border-emerald-400/30 bg-emerald-500/[0.03] hover:bg-emerald-500/10 text-emerald-100/80 inline-flex items-center justify-center gap-1 cursor-pointer"
              data-testid={`workbench-report-add-image-${rid}`}
            >
              <ImagePlus className="w-3 h-3" /> Add image
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) addImageSection(f, sections.length);
                }}
              />
            </label>
            <label
              className="text-[10px] px-2 py-1.5 rounded border border-dashed border-fuchsia-400/30 bg-fuchsia-500/[0.03] hover:bg-fuchsia-500/10 text-fuchsia-100/80 inline-flex items-center justify-center gap-1 cursor-pointer"
              data-testid={`workbench-report-import-pdf-${rid}`}
              title="Rasterise each PDF page and insert as image sections"
            >
              <FileImage className="w-3 h-3" /> Import PDF
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) importPdfSections(f, null);
                }}
              />
            </label>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={isRevising ? "opacity-60 pointer-events-none" : ""}>
      <div className="flex gap-1 px-3 pt-2 border-t border-cyan-500/10">
        {[
          { key: "read", label: "Read" },
          { key: "edit", label: "Edit sections" },
          { key: "split", label: "Split", icon: Columns },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`workbench-report-tab-${t.key}-${rid}`}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-t-md transition inline-flex items-center gap-1 ${
              tab === t.key
                ? "bg-cyan-500/15 text-cyan-100 border-b-2 border-cyan-400"
                : "text-[#647087] hover:text-[#a0aabf]"
            }`}
          >
            {t.icon ? <t.icon className="w-3 h-3" /> : null}
            {t.label}
          </button>
        ))}
      </div>
      {tab === "read" && renderReadPane()}
      {tab === "edit" && renderEditPane()}
      {tab === "split" && (
        <div
          className="flex flex-col lg:flex-row lg:divide-x lg:divide-white/10"
          data-testid={`workbench-report-split-${rid}`}
        >
          <div className="lg:w-1/2 lg:pr-1">
            <div className="text-[9px] uppercase tracking-wider text-[#647087] px-3 pt-2">Read</div>
            {renderReadPane()}
          </div>
          <div className="lg:w-1/2 lg:pl-1">
            <div className="text-[9px] uppercase tracking-wider text-[#647087] px-3 pt-2">Edit</div>
            {renderEditPane()}
          </div>
        </div>
      )}
    </div>
  );
}


function ReportRow({ draft, report, onChanged }) {
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(report.name || "");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [presetSeed, setPresetSeed] = useState(null);
  // Iter 278 — PDF presenter state machine. Replaces previewUrl /
  // setBusy('pdf') / window.open-then-pray. See `pdfFlow` definitions
  // below for full state-transition semantics. The cached blob is keyed
  // on `${report.id}::${report.updated_at}` so a refine click
  // automatically invalidates it.
  const [pdfPhase, setPdfPhase] = useState("idle"); // idle | generating | ready | error
  const [pdfElapsed, setPdfElapsed] = useState(0);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfBlobKey, setPdfBlobKey] = useState(null);
  const [pdfErr, setPdfErr] = useState(null); // {status, message}
  const [pdfShowDetail, setPdfShowDetail] = useState(false);
  // Iter 282 — Server-side cache warmth. Polls /pdf-status while
  // status is "warming" so by the time the rep clicks Export the
  // backend has already rendered + cached the PDF in Mongo. Removes
  // the 20-30s wait-at-click that trips Cloudflare 520 on production.
  const [pdfServerCache, setPdfServerCache] = useState("unknown"); // ready | warming | stale | missing | not_ready | unknown
  const [regenerating, setRegenerating] = useState(false);
  const _pdfAbortRef = useRef(null);
  const _pdfTimerRef = useRef(null);
  const _pdfPopupRef = useRef(null);
  // Iter 248 — Refinement loop. By default the "Draft" panel is OPEN
  // when the report first lands `ready` so the rep reads it before
  // exporting to PDF. Refine feedback is single-line free-text;
  // submitting flips status → "revising" and the BG worker rewrites
  // the body in place.
  const [draftOpen, setDraftOpen] = useState(true);
  const [feedback, setFeedback] = useState("");
  // [Iter 285] When voice dictation fails gracefully (quota out, audio
  // unclear, network blip), surface the reason inline above the
  // textarea — the toast alone gets missed mid-record.
  const [voiceBanner, setVoiceBanner] = useState(null);
  const [refining, setRefining] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Iter 427.p23 — Proposal identity (variant label + note + status).
  // Local state mirrors the report doc so the rep can edit label/note
  // inline; onBlur PATCHes and bubbles the update up.
  const [variantLabel, setVariantLabel] = useState(report.variant_label || "");
  const [variantNote, setVariantNote] = useState(report.variant_note || "");
  const [markingOfficial, setMarkingOfficial] = useState(false);
  useEffect(() => {
    setVariantLabel(report.variant_label || "");
    setVariantNote(report.variant_note || "");
  }, [report.variant_label, report.variant_note]);

  const proposalStatus = report.proposal_status || "draft";
  const isOfficial = proposalStatus === "official";

  const patchVariant = async (updates) => {
    try {
      const { data } = await api.patch(
        `/workbench/drafts/${draft.id}/reports/${report.id}/variant`,
        updates,
      );
      onChanged?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const markOfficial = async () => {
    if (markingOfficial) return;
    if (!draft.customer_id) {
      toast.error("Link the workbench to a customer before marking Official.");
      return;
    }
    setMarkingOfficial(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/reports/${report.id}/mark-official`,
        {},
      );
      toast.success(
        data?.project_id
          ? "Marked Official — sent to Customer docs and Project → Proposals."
          : "Marked Official — sent to Customer docs.",
      );
      onChanged?.({
        id: report.id,
        proposal_status: "official",
        official_document_id: data.customer_document_id,
        official_customer_id: data.customer_id,
        official_project_id: data.project_id,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark Official failed.");
    } finally {
      setMarkingOfficial(false);
    }
  };

  const unmarkOfficial = async () => {
    if (markingOfficial) return;
    if (!window.confirm(
      "Withdraw this Official proposal from Customer docs and Project → Proposals?\n\n"
      + "The workbench version stays intact — only the customer-facing PDF is removed.",
    )) return;
    setMarkingOfficial(true);
    try {
      await api.post(`/workbench/drafts/${draft.id}/reports/${report.id}/unmark-official`);
      toast.success("Withdrawn. Proposal is back to Draft in the workbench.");
      onChanged?.({
        id: report.id,
        proposal_status: "draft",
        official_document_id: null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unmark failed.");
    } finally {
      setMarkingOfficial(false);
    }
  };

  // Iter 427.p24 — Variant fork state + handler. Opens an inline
  // panel under the identity strip for the rep to enter the variant
  // note (what makes V2 different from V1) before firing the fork.
  const [forkOpen, setForkOpen] = useState(false);
  const [forkNote, setForkNote] = useState("");
  const [forkLabel, setForkLabel] = useState("");
  const [forking, setForking] = useState(false);
  // Iter 427.p30 — Pricing Source picker inside the Fork Panel. The
  // selected quote is the AUTHORITATIVE scope for the new variant, so
  // the LLM drops any stages/items not present in it.
  const [forkQuoteId, setForkQuoteId] = useState("");
  const [forkQuotes, setForkQuotes] = useState([]);
  const [forkQuotesLoading, setForkQuotesLoading] = useState(false);
  const [forkDetected, setForkDetected] = useState(null); // { detected_quote_id, detected_from }

  useEffect(() => {
    if (!forkOpen) return;
    let cancelled = false;
    (async () => {
      setForkQuotesLoading(true);
      try {
        const { data } = await api.get(
          `/workbench/drafts/${draft.id}/reports/${report.id}/fork-context`,
        );
        if (cancelled) return;
        setForkQuotes(data.quotes || []);
        setForkDetected(data.detected_quote_id ? {
          detected_quote_id: data.detected_quote_id,
          detected_from: data.detected_from,
        } : null);
      } catch {
        if (!cancelled) {
          setForkQuotes([]);
          setForkDetected(null);
        }
      } finally {
        if (!cancelled) setForkQuotesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [forkOpen, draft.id, report.id]);

  const submitFork = async () => {
    if (forking) return;
    const note = forkNote.trim();
    if (!note) {
      toast.error("Add a variant note (what makes this version different — e.g. 'phased, stage 1 only').");
      return;
    }
    setForking(true);
    try {
      const pricing_source = forkQuoteId
        ? { type: "quote", quote_id: forkQuoteId }
        : undefined;
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/reports/${report.id}/fork`,
        {
          variant_label: forkLabel.trim() || undefined,
          variant_note: note,
          length: report.length,
          audience: report.audience,
          pricing_source,
        },
      );
      const q = forkQuoteId ? forkQuotes.find((x) => x.id === forkQuoteId) : null;
      toast.success(
        `New variant "${data.variant_label}" is generating`
        + (q ? ` — scoped to ${q.doc_number || "selected quote"}.` : ".")
      );
      setForkOpen(false);
      setForkNote("");
      setForkLabel("");
      setForkQuoteId("");
      // Bubble a synthetic report-added event by pushing the new
      // report doc up; ReportsPanel will slot it into its list.
      onChanged?.({ id: data.id, ...data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fork failed.");
    } finally {
      setForking(false);
    }
  };

  // Iter 278 — Clean up the cached blob URL + any in-flight timer / abort
  // when the report row unmounts so we don't leak. Also abort an in-flight
  // PDF fetch.
  useEffect(() => () => {
    if (pdfBlobUrl) {
      try { window.URL.revokeObjectURL(pdfBlobUrl); } catch { /* ignore */ }
    }
    if (_pdfTimerRef.current) {
      clearInterval(_pdfTimerRef.current);
      _pdfTimerRef.current = null;
    }
    if (_pdfAbortRef.current) {
      try { _pdfAbortRef.current.abort(); } catch { /* ignore */ }
    }
  }, [pdfBlobUrl]);

  // [Iter 283c] Invalidate the cached blob whenever the cache key
  // shifts (refine bumps it). Without this, a stale `pdfBlobUrl` +
  // `pdfBlobKey` pair from BEFORE the refine still passes the
  // `pdfBlobKey === _cacheKey` guard if the comparison is sloppy —
  // and worse, the user sees a successful "open" that points at the
  // pre-refine bytes. Belt + braces.
  // NOTE: this effect MUST live AFTER `_cacheKey` is declared (line
  // ~1005) — placing it above causes a temporal-dead-zone crash in
  // production-minified bundles: "Cannot access 'U' before initialization".

  // Poll for status flips so the "Generating…" row turns into the real
  // headline once the BG worker writes the answer. Iter 248: also poll
  // while status === "revising" so the refine loop updates in place.
  useEffect(() => {
    if (report.status !== "running" && report.status !== "revising") return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/workbench/drafts/${draft.id}/reports/${report.id}`);
        if (cancelled) return;
        if (data && data.status !== "running" && data.status !== "revising") {
          onChanged?.(data);
          setRefining(false);
        }
      } catch { /* keep polling */ }
    };
    const handle = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(handle); };
  }, [report.id, report.status, draft.id, onChanged]);

  // Iter 282 — Pre-warm the PDF cache the moment a report becomes ready.
  // The endpoint is idempotent: hitting it returns the current cache
  // state and, on a miss, kicks off a background WeasyPrint render.
  // We poll until state === "ready" (or we hit ~120s). This makes the
  // user's eventual Export PDF click a sub-200ms cache hit instead of
  // a 20-30s cold render that risks tripping Cloudflare 520 on prod.
  useEffect(() => {
    if (!report?.id) return undefined;
    if (report.status !== "ready") {
      setPdfServerCache(report.status === "revising" ? "warming" : "unknown");
      return undefined;
    }
    let cancelled = false;
    let polls = 0;
    const check = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(
          `/workbench/drafts/${draft.id}/reports/${report.id}/pdf-status`,
        );
        if (cancelled) return;
        const state = data?.state || "unknown";
        setPdfServerCache(state);
        if (state === "ready") return;  // stop polling
        polls += 1;
        if (polls > 24) return;  // ~2min cap
        setTimeout(check, 5000);
      } catch {
        // Network blip — try once more, then give up silently.
        polls += 1;
        if (!cancelled && polls < 6) setTimeout(check, 5000);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [report?.id, report?.status, report?.updated_at, draft.id]);

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === report.name) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/workbench/drafts/${draft.id}/reports/${report.id}`,
        { name: trimmed },
      );
      onChanged?.(data);
      setRenaming(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/workbench/drafts/${draft.id}/reports/${report.id}`);
      onChanged?.({ ...report, _deleted: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  // [2026-02-28 Dirk] Soft cancel: flip the running compose/refine
  // to `cancelled` without deleting the row. User keeps whatever
  // partial state exists; next click of Generate starts fresh.
  const cancelGeneration = async () => {
    setBusy(true);
    try {
      await api.post(
        `/workbench/drafts/${draft.id}/reports/${report.id}/cancel`,
        {},
      );
      onChanged?.({ ...report, status: "cancelled" });
      toast.success("Generation cancelled. Click Generate to retry.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  // Iter 248 — Send refinement feedback to Hidro. Server pushes the
  // current body onto `revisions` and re-renders in the BG; we flip
  // local refining flag, surface a "revising…" banner, and the existing
  // poll picks the rewritten body up automatically.
  const submitRefine = async () => {
    const fb = feedback.trim();
    if (!fb || fb.length < 2) {
      toast.error("Tell Hidro what to change first.");
      return;
    }
    // Soft cap matching the backend (15 000). Anything beyond that
    // would be rejected with a 422 — surface the limit BEFORE we POST
    // so the rep can trim instead of losing a long voice rant to a
    // silent 422.
    if (fb.length > 15_000) {
      toast.error(`Feedback is ${fb.length.toLocaleString()} chars — trim it under 15,000 to send.`);
      return;
    }
    setRefining(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/reports/${report.id}/refine`,
        { feedback: fb },
      );
      onChanged?.(data);
      setFeedback("");
      // Keep the draft open so the rep watches the rewrite happen.
      setDraftOpen(true);
    } catch (e) {
      setRefining(false);
      toast.error(
        formatErr(e?.response?.data?.detail, "Couldn't send refine — try again."),
      );
    }
  };

  // Iter 290e — Surgical restore of a prior revision. Pushes current
  // body onto the trail so this restore is itself reversible.
  const submitRestore = async (revisionIndex, label) => {
    if (!Number.isInteger(revisionIndex) || revisionIndex < 0) return;
    if (!window.confirm(`Restore this version?\n\nYour current text will be saved to history so this is reversible. Continue?`)) {
      return;
    }
    setRefining(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/reports/${report.id}/restore`,
        { revision_index: revisionIndex },
      );
      onChanged?.(data);
      toast.success(label ? `Restored ${label}` : "Restored prior version");
      setDraftOpen(true);
    } catch (e) {
      toast.error(
        formatErr(e?.response?.data?.detail, "Couldn't restore — try again."),
      );
    } finally {
      setRefining(false);
    }
  };

  // ═══ Iter 278 — PDF flow (fetch-then-present) ═════════════════════
  //
  // Why this exists: attempts 1-13 all used the same broken pattern —
  // navigate the browser to /api/.../pdf?auth=… and pray. WeasyPrint
  // can take 5-30s on a busy production pod; during that wait the user
  // sees a blank tab. If anything fails (SW timeout, nginx 502,
  // pop-up blocked, JWT rejected) the user just gets a dark screen or
  // a Chrome "site wasn't available" with no Retry button.
  //
  // The new pattern (attempt 14):
  //   • CLICK → synchronously open a blank popup (keeps user-gesture
  //     so pop-up blockers leave it alone). Write a branded "Building
  //     your PDF…" loading page into it.
  //   • Fetch the PDF as a blob with a 60s AbortController timeout +
  //     visible elapsed counter in the inline panel.
  //   • On SUCCESS: createObjectURL(blob) → popup.location.replace()
  //     (instant render in the already-open tab, no second pop-up
  //     needed) AND cache the blob URL locally so Download / Share
  //     use the SAME blob — no second WeasyPrint render.
  //   • On ERROR / TIMEOUT: close the popup, flip the inline panel to
  //     red state with the HTTP status and a [Try again] button. NO
  //     blank screen, NO mystery silence.
  //   • Cache key = `${report.id}::${report.updated_at}`. Refining a
  //     report bumps updated_at → cached blob is invalidated → next
  //     click re-fetches.
  // -----------------------------------------------------------------

  const _safeFilename = () => {
    const base = (report.name || report.headline || "workbench-report")
      .replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
    return (base || "workbench-report") + ".pdf";
  };

  // [Iter 283c] Cache key composed of report.id + EVERY mutation
  // timestamp on the report. Refine bumps `ready_at` AND `updated_at`
  // (iter 283c), but we belt-and-brace by including both — that way
  // even if a server-side path forgets to bump one, the cache still
  // invalidates. Length is irrelevant; this string only needs to
  // uniquely identify a single body+headline state.
  const _cacheKey = `${report.id}::${report.updated_at || ""}::${report.ready_at || ""}`;

  // [Iter 283c] Invalidate the cached blob whenever the cache key
  // shifts (refine bumps both timestamps in the key). Without this,
  // a stale `pdfBlobUrl` from BEFORE the refine could still leak
  // through if a code path bypassed the `pdfBlobKey === _cacheKey`
  // guard. Belt + braces. Must live AFTER `_cacheKey` is declared.
  useEffect(() => {
    if (pdfBlobKey && pdfBlobKey !== _cacheKey) {
      if (pdfBlobUrl) {
        try { window.URL.revokeObjectURL(pdfBlobUrl); } catch { /* ignore */ }
      }
      setPdfBlobUrl(null);
      setPdfBlobKey(null);
      setPdfPhase("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_cacheKey]);

  // HTML written into the freshly-opened popup so the new tab isn't
  // blank-and-confusing while WeasyPrint renders.
  const _popupLoadingHtml = () => (
    `<!doctype html><html lang="en"><head><meta charset="utf-8"`
    + `><title>${_esc(report.name || "Workbench report")}…</title>`
    + `<style>html,body{background:#0b1320;color:#e5e7eb;font-family:system-ui,sans-serif;margin:0;height:100%;display:flex;align-items:center;justify-content:center}.card{text-align:center;padding:32px;max-width:420px}h1{font-size:18px;margin:0 0 8px;font-weight:600}p{margin:8px 0;color:#94a3b8;font-size:13px;line-height:1.5}.spin{display:inline-block;width:36px;height:36px;border:3px solid #1fb6c1;border-right-color:transparent;border-radius:50%;animation:s 1s linear infinite;margin-bottom:14px}@keyframes s{to{transform:rotate(360deg)}}</style></head>`
    + `<body><div class="card"><div class="spin"></div>`
    + `<h1>Building your PDF…</h1>`
    + `<p>${_esc(report.name || "Engineering report")}</p>`
    + `<p style="color:#475569;font-size:11px">Server can take up to a minute on busy days. This tab will refresh automatically when it's ready. If something goes wrong you'll see a retry option in the original tab.</p>`
    + `</div></body></html>`
  );

  const _resetPdfState = () => {
    if (_pdfTimerRef.current) {
      clearInterval(_pdfTimerRef.current);
      _pdfTimerRef.current = null;
    }
    _pdfAbortRef.current = null;
    _pdfPopupRef.current = null;
    setPdfElapsed(0);
  };

  const cancelPdf = () => {
    if (_pdfAbortRef.current) {
      try { _pdfAbortRef.current.abort(); } catch { /* ignore */ }
    }
    if (_pdfPopupRef.current && !_pdfPopupRef.current.closed) {
      try { _pdfPopupRef.current.close(); } catch { /* ignore */ }
    }
    _resetPdfState();
    setPdfPhase("idle");
    setPdfErr(null);
  };

  // Core fetch — returns Blob on success, throws on failure / abort.
  // Uses fetch() (not axios) so we can hook AbortController cleanly.
  const _fetchPdfBlob = async () => {
    const tok = (typeof localStorage !== "undefined" && localStorage.getItem("hv_token")) || "";
    const url = `${API}/workbench/drafts/${draft.id}/reports/${report.id}/pdf${tok ? `?auth=${encodeURIComponent(tok)}` : ""}`;
    const ctrl = new AbortController();
    _pdfAbortRef.current = ctrl;
    // 60-second hard timeout.
    const killer = setTimeout(() => { try { ctrl.abort("timeout"); } catch { /* ignore */ } }, 60_000);
    try {
      const res = await fetch(url, {
        signal: ctrl.signal,
        headers: { Authorization: tok ? `Bearer ${tok}` : "" },
        credentials: "include",
        // Tell the SW to skip its 4s navigation-timeout fallback even
        // if someone breaks the nav-handler exclusion later.
        cache: "no-store",
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => "");
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        err.body = errBody;
        throw err;
      }
      return await res.blob();
    } finally {
      clearTimeout(killer);
    }
  };

  // Main "Export PDF" handler — keeps user-gesture for the popup.
  const openPdf = async () => {
    // 🟢 Cache hit — instant open. The blob URL was created on the
    // first generate; subsequent clicks navigate to it directly with
    // a fresh user-gesture pop-up that Chrome never blocks.
    if (pdfPhase === "ready" && pdfBlobUrl && pdfBlobKey === _cacheKey) {
      const w = window.open(pdfBlobUrl, "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Pop-up blocked — allow pop-ups, then click again.");
      }
      return;
    }
    // Already generating — clicking again does nothing.
    if (pdfPhase === "generating") return;

    // 1) Synchronously open the popup in the current user-gesture.
    //    Pop-up blockers explicitly allow this.
    const popup = window.open("about:blank", "_blank");
    if (popup) {
      try {
        popup.document.open();
        popup.document.write(_popupLoadingHtml());
        popup.document.close();
      } catch { /* some browsers throw on cross-origin write; harmless */ }
    }
    _pdfPopupRef.current = popup;

    // 2) Flip state to generating, start the elapsed timer.
    setPdfErr(null);
    setPdfShowDetail(false);
    setPdfPhase("generating");
    setPdfElapsed(0);
    if (_pdfTimerRef.current) clearInterval(_pdfTimerRef.current);
    _pdfTimerRef.current = setInterval(() => setPdfElapsed((s) => s + 1), 1000);

    // 3) Fetch the PDF as a blob with 60s timeout.
    try {
      const blob = await _fetchPdfBlob();
      // Success — make the blob URL, redirect the already-open popup,
      // cache for instant subsequent opens.
      const objUrl = window.URL.createObjectURL(blob);
      // Revoke any previous cached blob to avoid leaks.
      if (pdfBlobUrl) {
        try { window.URL.revokeObjectURL(pdfBlobUrl); } catch { /* ignore */ }
      }
      setPdfBlobUrl(objUrl);
      setPdfBlobKey(_cacheKey);
      setPdfPhase("ready");
      _resetPdfState();
      if (popup && !popup.closed) {
        try { popup.location.replace(objUrl); } catch { /* ignore */ }
      }
      toast.success("PDF ready");
    } catch (e) {
      // Failure — close popup (no point leaving a forever-loading tab
      // open) and flip to error state in the main tab.
      if (popup && !popup.closed) {
        try { popup.close(); } catch { /* ignore */ }
      }
      const isAbort = e?.name === "AbortError" || /aborted/i.test(e?.message || "");
      const isTimeout = isAbort && pdfElapsed >= 58;
      _resetPdfState();
      if (isAbort && !isTimeout) {
        // User clicked Cancel — silent return to idle.
        setPdfPhase("idle");
        return;
      }
      setPdfErr({
        status: e?.status || (isTimeout ? "TIMEOUT" : "NETWORK"),
        message: isTimeout
          ? "The PDF took longer than 60 seconds to generate. The server may be busy."
          : (e?.message || "We couldn't reach the PDF service."),
        body: e?.body || "",
      });
      setPdfPhase("error");
    }
  };

  // Download — uses the cached blob if available; otherwise generates
  // one via the same flow (no popup needed for downloads).
  // [Iter 283] Removed downloadPdf / sharePdf / _iosShareFetch /
  // sendToCustomer — the new PDF tab exposes the browser's native
  // download + share + print, and the rep asked for "just keep the
  // export, remove the rest". `_esc` is still used by the popup
  // loading HTML so it stays.
  const _esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

  const isRunning = report.status === "running";
  const isRevising = report.status === "revising" || refining;
  const isError = report.status === "error";
  const hasBody = !!(report.body && report.body.trim());
  const revisions = Array.isArray(report.revisions) ? report.revisions : [];

  return (
    <div
      className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2"
      data-testid={`workbench-report-${report.id}`}
    >
      <div className="flex items-start gap-2">
        <FileText className={`w-4 h-4 mt-0.5 shrink-0 ${isError ? "text-rose-300" : "text-cyan-300"}`} />
        <div className="flex-1 min-w-0">
          {renaming ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setRenaming(false); }}
                className="hv-input text-xs flex-1"
                data-testid={`workbench-report-rename-input-${report.id}`}
              />
              <button onClick={saveName} disabled={busy} className="hv-btn hv-btn-ghost text-xs" data-testid={`workbench-report-rename-save-${report.id}`}>
                <Save className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium truncate" data-testid={`workbench-report-name-${report.id}`}>
                {report.name}
              </span>
              <button
                onClick={() => { setName(report.name || ""); setRenaming(true); }}
                className="text-[#647087] hover:text-cyan-300 p-0.5"
                data-testid={`workbench-report-rename-btn-${report.id}`}
                title="Rename"
              >
                <Edit3 className="w-3 h-3" />
              </button>
              {report.status === "ready" && (
                <button
                  onClick={() => setPresetSeed({ title: report.name || report.headline || "", kinds: draft.kind ? [draft.kind] : [], seedExample: { report_id: report.id, filename: report.name || report.headline || "Workbench report" }, autoWrite: true })}
                  className="text-[#647087] hover:text-cyan-300 p-0.5"
                  data-testid={`workbench-report-save-preset-${report.id}`}
                  title="Save as preset"
                >
                  <BookmarkPlus className="w-3 h-3" />
                </button>
              )}
              {presetSeed && <PresetLibraryDialog open seed={presetSeed} onOpenChange={(o) => { if (!o) setPresetSeed(null); }} />}
            </div>
          )}
          <div className="text-[10px] text-[#647087] mt-0.5">
            {report.stage && (
              <>
                <span className="uppercase tracking-wider text-amber-200/80">{report.stage}</span>
                <span> · </span>
              </>
            )}
            <span className="uppercase tracking-wider text-cyan-200/80">{report.audience || "customer"}</span>
            <span> · </span>
            <span className="uppercase tracking-wider">{report.length}</span>
            {report.headline && <span> · {report.headline}</span>}
            {report.sent_at && (
              <span className="text-emerald-300"> · sent to customer</span>
            )}
          </div>
          {/* Iter 427.p23 — Proposal identity strip. Status pill +
              variant label / note editor + Mark/Unmark Official.
              Renders on every report row so the family view reads
              at a glance which proposal is the customer's current
              reference. */}
          <div
            className="mt-1.5 flex flex-wrap items-center gap-1.5"
            data-testid={`workbench-report-identity-${report.id}`}
          >
            <span
              className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                isOfficial
                  ? "bg-emerald-500/15 border-emerald-400/50 text-emerald-100"
                  : proposalStatus === "superseded"
                  ? "bg-white/5 border-white/15 text-[#a0aabf] line-through"
                  : "bg-white/5 border-white/15 text-[#a0aabf]"
              }`}
              data-testid={`workbench-report-status-pill-${report.id}`}
            >
              {proposalStatus === "official" ? "✓ Official" : proposalStatus}
            </span>
            <input
              type="text"
              value={variantLabel}
              onChange={(e) => setVariantLabel(e.target.value)}
              onBlur={() => {
                if (variantLabel !== (report.variant_label || "")) {
                  patchVariant({ variant_label: variantLabel });
                }
              }}
              placeholder="Variant label (e.g. V1 — Full scope)"
              className="text-[10px] bg-transparent border-b border-white/10 focus:border-cyan-400/40 outline-none text-[#cbd5e1] flex-1 min-w-[140px]"
              data-testid={`workbench-report-variant-label-${report.id}`}
            />
            <input
              type="text"
              value={variantNote}
              onChange={(e) => setVariantNote(e.target.value)}
              onBlur={() => {
                if (variantNote !== (report.variant_note || "")) {
                  patchVariant({ variant_note: variantNote });
                }
              }}
              placeholder="Variant note (phased, alt spec, etc.)"
              className="text-[10px] bg-transparent border-b border-white/10 focus:border-cyan-400/40 outline-none text-[#cbd5e1] flex-1 min-w-[160px]"
              data-testid={`workbench-report-variant-note-${report.id}`}
            />
            {!isOfficial ? (
              <button
                type="button"
                onClick={markOfficial}
                disabled={markingOfficial || report.status !== "ready" || !draft.customer_id}
                title={
                  !draft.customer_id
                    ? "Link a customer to the workbench first."
                    : report.status !== "ready"
                    ? "Wait for the report to finish generating."
                    : "Promote this proposal to Official and fan it out to Customer docs (and Project → Proposals when linked)."
                }
                className="text-[10px] px-2 py-0.5 rounded border border-emerald-400/50 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-100 disabled:opacity-40 inline-flex items-center gap-1"
                data-testid={`workbench-report-mark-official-${report.id}`}
              >
                {markingOfficial ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Mark Official
              </button>
            ) : (
              <>
                <span
                  className="text-[10px] text-emerald-200/80 inline-flex items-center gap-0.5"
                  data-testid={`workbench-report-official-chip-customer-${report.id}`}
                  title={`Customer document: ${report.official_document_id || ""}`}
                >
                  → Customer docs
                </span>
                {report.official_project_id && (
                  <span
                    className="text-[10px] text-emerald-200/80 inline-flex items-center gap-0.5"
                    data-testid={`workbench-report-official-chip-project-${report.id}`}
                  >
                    · → Project → Proposals
                  </span>
                )}
                <button
                  type="button"
                  onClick={unmarkOfficial}
                  disabled={markingOfficial}
                  className="text-[10px] px-2 py-0.5 rounded border border-white/15 bg-black/20 hover:bg-black/40 text-[#a0aabf] disabled:opacity-40 inline-flex items-center gap-1"
                  data-testid={`workbench-report-unmark-official-${report.id}`}
                  title="Withdraw from Customer docs and Project → Proposals. The workbench version stays."
                >
                  {markingOfficial ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                  Withdraw
                </button>
              </>
            )}
            {/* Iter 427.p24 — Fork button. Creates a new variant that
                inherits Brainstorm context + this proposal's compiled
                body as a coherence anchor. Only shows on ready reports
                since the LLM needs the parent body to align to. */}
            {report.status === "ready" && (
              <button
                type="button"
                onClick={() => setForkOpen((v) => !v)}
                data-testid={`workbench-report-fork-toggle-${report.id}`}
                className="text-[10px] px-2 py-0.5 rounded border border-fuchsia-400/40 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-100 inline-flex items-center gap-1"
                title="Fork a new variant of this proposal (e.g. phased, cheaper, alt spec). Keeps this one as the family reference."
              >
                <Copy className="w-3 h-3" />
                {forkOpen ? "Cancel fork" : "Fork variant"}
              </button>
            )}
            {(report.sibling_ids && report.sibling_ids.length > 0) && (
              <span
                className="text-[9px] uppercase tracking-wider text-[#647087]"
                data-testid={`workbench-report-siblings-count-${report.id}`}
                title={`Siblings: ${report.sibling_ids.join(", ")}`}
              >
                · {report.sibling_ids.length} sibling{report.sibling_ids.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          {/* Fork panel — inline form so the rep can enter the variant
              note (what makes V2 different) before submitting. */}
          {forkOpen && (
            <div
              className="mt-2 rounded-md border border-fuchsia-400/30 bg-fuchsia-500/[0.05] p-2 space-y-1.5"
              data-testid={`workbench-report-fork-panel-${report.id}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-fuchsia-200/80 flex items-center gap-1">
                <Copy className="w-3 h-3" /> Fork this proposal
              </div>
              <input
                type="text"
                value={forkLabel}
                onChange={(e) => setForkLabel(e.target.value)}
                placeholder="Variant label (e.g. V2 — Phased stage 1)"
                disabled={forking}
                data-testid={`workbench-report-fork-label-${report.id}`}
                className="w-full bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-fuchsia-400/40 border border-fuchsia-400/20"
              />
              <DictTextarea
                value={forkNote}
                onChange={(e) => setForkNote(e.target.value)}
                placeholder="What makes this variant different? (e.g. 'phased — only stage 1 UF replacement, carbon deferred to phase 2 pending budget')"
                disabled={forking}
                data-testid={`workbench-report-fork-note-${report.id}`}
                className="w-full bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-fuchsia-400/40 border border-fuchsia-400/20 min-h-[60px] resize-y"
              />
              {/* Iter 427.p30 — Pricing Source picker. When set, the
                  selected quote becomes the AUTHORITATIVE scope for
                  the new variant (stages/items not in the quote are
                  dropped). Auto-detect chip surfaces the quote number
                  mentioned in the parent body or brainstorm. */}
              <div className="pt-1 border-t border-fuchsia-400/15 space-y-1">
                <div className="text-[9px] uppercase tracking-wider text-fuchsia-200/70 flex items-center justify-between gap-2">
                  <span>Pricing source (defines scope) — optional</span>
                  {forkQuotesLoading && (
                    <Loader2 className="w-3 h-3 animate-spin text-fuchsia-300/60" />
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <select
                    value={forkQuoteId}
                    onChange={(e) => setForkQuoteId(e.target.value)}
                    disabled={forking || forkQuotesLoading}
                    data-testid={`workbench-report-fork-quote-select-${report.id}`}
                    className="flex-1 min-w-[160px] bg-black/25 rounded px-2 py-1 text-[11px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-fuchsia-400/40 border border-fuchsia-400/20"
                  >
                    <option value="">— No pricing source (free-scope) —</option>
                    {forkQuotes.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.doc_number || q.id.slice(0, 8)}
                        {q.title ? ` · ${q.title}` : ""}
                        {q.total_incl_vat ? ` · R${Number(q.total_incl_vat).toLocaleString()}` : ""}
                        {q.status ? ` · ${q.status}` : ""}
                      </option>
                    ))}
                  </select>
                  {forkDetected?.detected_quote_id && forkDetected.detected_quote_id !== forkQuoteId && (
                    <button
                      type="button"
                      onClick={() => setForkQuoteId(forkDetected.detected_quote_id)}
                      disabled={forking}
                      data-testid={`workbench-report-fork-quote-detect-${report.id}`}
                      title={`Detected in ${forkDetected.detected_from === "parent_body" ? "the parent proposal" : "the brainstorm chat"} — click to select.`}
                      className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-100 inline-flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3" />
                      Use {(forkQuotes.find((q) => q.id === forkDetected.detected_quote_id)?.doc_number) || "detected quote"}
                    </button>
                  )}
                </div>
                {forkQuoteId && (
                  <div
                    className="text-[10px] text-emerald-200/80 leading-snug"
                    data-testid={`workbench-report-fork-quote-hint-${report.id}`}
                  >
                    ✓ Scoped to this quote — any stage or item not in it will be dropped from the new variant.
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={submitFork}
                  disabled={forking || !forkNote.trim()}
                  data-testid={`workbench-report-fork-submit-${report.id}`}
                  className="text-[10px] px-2 py-1 rounded border border-fuchsia-400/50 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  {forking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
                  Fork &amp; generate
                </button>
                <span className="text-[10px] text-[#647087]">
                  Uses full brainstorm{forkQuoteId ? " + selected quote as authoritative scope" : " + this proposal's body as context"}.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Iter 427.p25 — Pending refresh card renders under the
          identity strip when the server has a staged non-destructive
          refresh. Rep reviews the section-level diff and accepts /
          rejects individually. */}
      <PendingRefreshCard
        draftId={draft.id}
        report={report}
        onChanged={onChanged}
      />

      {/* Iter 427.p32 — Per-report appendix overrides. Compact
          collapsible panel; only shown once the report is ready
          (before that there's nothing meaningful to attach). */}
      {report.status === "ready" && (
        <ReportAppendixOverridesPanel
          draftId={draft.id}
          report={report}
        />
      )}

      {isRunning && (
        <div className="text-[11px] text-[#a0aabf] flex items-center gap-2" data-testid={`workbench-report-status-${report.id}`}>
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>Generating · this usually takes 15-40 seconds…</span>
          {/* [Iter 290zp.12g] Always-available escape hatch. Reports
              spawned just before a deploy / worker restart get orphaned
              at status="running" with no BG task actually alive — the
              backend's 5-min stale-sweep eventually flips them to
              error, but the rep wants to kill them NOW. Two-click
              confirm to avoid accidental cancels during a normal 30s
              wait. */}
          {confirmDelete ? (
            <span className="inline-flex items-center gap-1 ml-auto">
              <button
                type="button"
                onClick={cancelGeneration}
                disabled={busy}
                className="hv-btn hv-btn-ghost text-[10px] text-rose-300"
                data-testid={`workbench-report-cancel-confirm-${report.id}`}
              >
                Confirm cancel
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-[10px] text-[#647087] hover:text-white px-1"
                data-testid={`workbench-report-cancel-cancel-${report.id}`}
              >
                keep waiting
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-[10px] text-[#647087] hover:text-rose-300 ml-auto"
              data-testid={`workbench-report-cancel-${report.id}`}
              title="Cancel this generation (keeps the row for retry)"
            >
              <Trash2 className="w-3 h-3" />
              Cancel
            </button>
          )}
        </div>
      )}
      {isRevising && !isRunning && (
        <div className="text-[11px] text-amber-200 flex items-center gap-2" data-testid={`workbench-report-revising-${report.id}`}>
          <Loader2 className="w-3 h-3 animate-spin" />
          Hidro is rewriting the draft with your feedback…
        </div>
      )}
      {isError && (
        <div className="text-[11px] text-rose-200" data-testid={`workbench-report-error-${report.id}`}>
          {report.body || "Generation failed."}
        </div>
      )}
      {report.refine_error && !isRevising && (
        <div className="text-[11px] text-amber-300 flex items-start gap-1.5" data-testid={`workbench-report-refine-error-${report.id}`}>
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Last refine failed: {report.refine_error}. The previous draft is still here — try again with sharper wording.</span>
        </div>
      )}

      {/* Iter 248 — Draft preview + refinement chat. The rep reviews
          the text BEFORE the PDF is generated; feedback updates the
          body in place via the refine BG worker. PDF export stays a
          one-click action below the refinement controls. */}
      {/* Iter 427.p18 — Hallucination detector amber banner.
          Flags fake boilerplate (Exclusions/Assumptions with
          no source content), internal costing methodology
          leaking (PRC, absorbed allocations, load-in factors),
          and product SKUs the LLM slipped in. Every finding
          is a concrete match to a banned pattern from real
          hallucinated output — the rep should re-generate or
          hand-edit. */}
      {!isRunning && !isError && hasBody && Array.isArray(report.section_drop_warnings) && report.section_drop_warnings.length > 0 && (
        <div
          className="rounded-md border border-rose-400/60 bg-rose-500/10 text-rose-100 text-[11px] px-2.5 py-2 flex items-start gap-2"
          data-testid={`workbench-report-section-drop-${report.id}`}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">Content dropped from the brainstorm</div>
            <div className="text-rose-200/90 mt-1 space-y-1">
              {report.section_drop_warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
            <div className="text-rose-200/70 text-[10px] mt-1.5">
              The length framing is prose-density only — content inclusion is non-negotiable. If this list is non-empty the LLM silently omitted sections/options. Refine or regenerate before PDF export.
            </div>
          </div>
        </div>
      )}
      {!isRunning && !isError && hasBody && report.hallucination_check?.count > 0 && (
        <div
          className="rounded-md border border-amber-400/50 bg-amber-500/10 text-amber-100 text-[11px] px-2.5 py-2 flex items-start gap-2"
          data-testid={`workbench-report-hallucination-${report.id}`}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              {report.hallucination_check.count} hallucinated pattern{report.hallucination_check.count === 1 ? "" : "s"} detected in this report.
            </div>
            <div className="text-amber-200/85 mt-1 space-y-0.5">
              {(report.hallucination_check.items || []).slice(0, 6).map((it, idx) => (
                <div key={idx} className="truncate">
                  <span className="text-amber-200/60">[{it.kind}]</span> "{it.match}"
                </div>
              ))}
              {report.hallucination_check.count > 6 && (
                <div className="text-amber-200/60">…and {report.hallucination_check.count - 6} more</div>
              )}
            </div>
            <div className="text-amber-200/70 text-[10px] mt-1.5">
              Fake Exclusions/Assumptions sections, internal costing methodology, or product SKUs are leaking into a customer-facing document. Regenerate or hand-edit the draft below before exporting.
            </div>
          </div>
        </div>
      )}
      {!isRunning && !isError && hasBody && (
        <div className="rounded-md border border-cyan-500/20 bg-cyan-500/[0.04]" data-testid={`workbench-report-draft-${report.id}`}>
          <button
            type="button"
            onClick={() => setDraftOpen((o) => !o)}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[10px] uppercase tracking-wider text-cyan-200/80 hover:text-cyan-100"
            data-testid={`workbench-report-draft-toggle-${report.id}`}
          >
            <FileText className="w-3 h-3" />
            Draft text
            {revisions.length > 0 && (
              <span className="text-[9px] text-amber-200/70 normal-case tracking-normal">
                · v{revisions.length + 1}
              </span>
            )}
            <span className="ml-auto">
              {draftOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </span>
          </button>
          {draftOpen && (
            <>
              {/* Iter 427.p21 — Read | Edit tabs. Read = current
                  flowing document view (simple reports never leave
                  this tab). Edit = section cards, each with heading
                  + body + per-section refine chat. Both views work
                  off the same section data model; the concatenated
                  markdown stays in `report.body` so PDF export is
                  unchanged. */}
              <ReportSectionTabs
                report={report}
                did={draft.id}
                onChanged={onChanged}
                isRevising={isRevising}
              />
              {/* Refine input */}
              <div className="px-2.5 pb-2.5 pt-2 border-t border-cyan-500/10 space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider text-amber-200/80 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  Refine with Hidro
                </label>
                {voiceBanner && (
                  <div
                    className="flex items-start gap-1.5 rounded-md border border-amber-400/40 bg-amber-500/10 text-amber-100 text-[11px] px-2 py-1.5"
                    data-testid={`workbench-report-voice-banner-${report.id}`}
                  >
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <div className="flex-1 leading-snug">{voiceBanner}</div>
                    <button
                      type="button"
                      onClick={() => setVoiceBanner(null)}
                      className="text-amber-200/60 hover:text-amber-100 text-[10px] shrink-0"
                      data-testid={`workbench-report-voice-banner-dismiss-${report.id}`}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2">
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="e.g. 'Add a section on filtration savings', 'Tone down the engineering jargon', 'Lead with the panel safety issue'…"
                    className="hv-input text-[11px] flex-1 min-h-[60px] resize-y"
                    disabled={isRevising}
                    data-testid={`workbench-report-refine-input-${report.id}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitRefine();
                    }}
                  />
                  <div className="flex flex-row sm:flex-col items-stretch gap-1.5 shrink-0">
                    <VoiceDictation
                      disabled={isRevising}
                      onText={(t) => { setVoiceBanner(null); setFeedback((prev) => (prev ? `${prev.trimEnd()} ${t}` : t)); }}
                      onGracefulFail={(msg) => setVoiceBanner(msg)}
                      testId={`workbench-report-refine-mic-${report.id}`}
                    />
                    <button
                      type="button"
                      onClick={submitRefine}
                      disabled={isRevising || feedback.trim().length < 2}
                      className="hv-btn hv-btn-ghost text-[11px] disabled:opacity-40"
                      data-testid={`workbench-report-refine-submit-${report.id}`}
                      title="Apply this feedback (⌘/Ctrl + Enter)"
                    >
                      {isRevising ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                      Refine
                    </button>
                  </div>
                </div>
                {revisions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowHistory((s) => !s)}
                    className="text-[10px] text-[#647087] hover:text-cyan-200"
                    data-testid={`workbench-report-history-toggle-${report.id}`}
                  >
                    {showHistory ? "Hide" : "Show"} revision history ({revisions.length})
                  </button>
                )}
                {feedback.trim().length >= 1500 && (
                  <div
                    className={`text-[10px] ${feedback.length > 15_000 ? "text-rose-300" : "text-[#647087]"}`}
                    data-testid={`workbench-report-refine-charcount-${report.id}`}
                  >
                    {feedback.length.toLocaleString()} / 15,000 characters
                    {feedback.length > 15_000 && " · trim before sending"}
                  </div>
                )}
                {showHistory && revisions.length > 0 && (
                  <ul
                    className="text-[10px] text-[#a0aabf] space-y-2 pl-0 list-none"
                    data-testid={`workbench-report-history-${report.id}`}
                  >
                    {revisions.slice().reverse().map((r, i) => {
                      // revisions[] is chronological — oldest at index 0.
                      // After reverse(), `i` is "0 = most recent prior",
                      // so the real chronological index is the reversed
                      // position. Restore endpoint expects the
                      // chronological (un-reversed) index.
                      const chronoIdx = revisions.length - 1 - i;
                      const versionLabel = `v${chronoIdx + 1}`;
                      const bodyPreview = (r.body || "").trim();
                      const previewSnippet = bodyPreview
                        ? bodyPreview.slice(0, 240).replace(/\s+/g, " ")
                        : "";
                      const ts = r.at ? new Date(r.at).toLocaleString() : "";
                      return (
                        <li
                          key={r.at || i}
                          className="border border-white/10 rounded p-2 bg-white/[0.02] space-y-1.5"
                          data-testid={`workbench-report-revision-${report.id}-${chronoIdx}`}
                        >
                          {/* Row 1 — version + full-width feedback (wraps,
                              never truncates so mobile can read it). */}
                          <div className="flex items-start gap-2 min-w-0">
                            <span className="text-cyan-200 font-mono text-[10px] flex-shrink-0 pt-px">{versionLabel}</span>
                            <span className="text-amber-200 text-[10px] flex-1 min-w-0 break-words leading-snug">
                              &ldquo;{(r.feedback || "—").slice(0, 200)}&rdquo;
                            </span>
                          </div>
                          {/* Row 2 — body preview (also wraps). */}
                          {previewSnippet && (
                            <div className="text-[10px] text-[#647087] italic leading-snug break-words">
                              {previewSnippet}
                              {bodyPreview.length > 240 ? "…" : ""}
                            </div>
                          )}
                          {/* Row 3 — timestamp + Restore button. Both
                              flexible so the button never crowds the
                              date on phones. */}
                          <div className="flex items-center justify-between gap-2 pt-0.5">
                            <span className="text-[9px] text-[#647087] whitespace-nowrap">{ts}</span>
                            {bodyPreview && (
                              <button
                                type="button"
                                onClick={() => submitRestore(chronoIdx, versionLabel)}
                                disabled={isRevising}
                                className="hv-btn hv-btn-ghost text-[10px] py-0.5 px-2 disabled:opacity-40 flex-shrink-0"
                                data-testid={`workbench-report-restore-${report.id}-${chronoIdx}`}
                                title="Restore this version. Identical-content restores are no-ops; the revisions trail also self-deduplicates."
                              >
                                ↩ Restore
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!isRunning && (
        <div className="flex flex-wrap gap-2 pt-1">
          {/* [Iter 282] Server-side warm indicator — tells the rep at a
              glance whether clicking Export will be instant (ready) or
              will trigger a render server-side (warming). Skipped when
              status is unknown (won't render until the poll lands). */}
          {pdfServerCache === "warming" && (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-100/60 dark:bg-amber-500/15 border border-amber-300/60 dark:border-amber-500/30 rounded-full px-2 py-0.5"
              data-testid={`workbench-report-pdf-warming-${report.id}`}
              title="The PDF is being pre-rendered on the server. Click Export when this turns green."
            >
              <Loader2 className="w-3 h-3 animate-spin" />
              Preparing PDF…
            </span>
          )}
          {pdfServerCache === "ready" && (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-300 bg-emerald-100/60 dark:bg-emerald-500/15 border border-emerald-300/60 dark:border-emerald-500/30 rounded-full px-2 py-0.5"
              data-testid={`workbench-report-pdf-warm-${report.id}`}
              title="PDF is cached server-side — Export will be instant."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              PDF ready
            </span>
          )}
          {/* Iter 427.p33 — Stale-cache chip. The cache has a PDF,
              but it was rendered before recent refines or attachment
              changes. Rep should click Regenerate before Export. */}
          {pdfServerCache === "stale" && (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] text-orange-700 dark:text-orange-300 bg-orange-100/60 dark:bg-orange-500/15 border border-orange-300/60 dark:border-orange-500/30 rounded-full px-2 py-0.5"
              data-testid={`workbench-report-pdf-stale-${report.id}`}
              title="The cached PDF is out of date. Click Regenerate to rebuild it."
            >
              <RefreshCw className="w-3 h-3" />
              PDF out of date
            </span>
          )}
          {/* Iter 427.p33 — Regenerate PDF button. Force-clears the
              server cache and warms a fresh render. Available once
              the report is ready; skipped while generating so the
              rep can't clobber an in-flight render. */}
          {report.status === "ready" && (
            <button
              type="button"
              onClick={async () => {
                if (regenerating) return;
                setRegenerating(true);
                setPdfServerCache("warming");
                try {
                  await api.post(
                    `/workbench/drafts/${draft.id}/reports/${report.id}/pdf/regenerate`,
                  );
                  toast.success("Regenerating PDF…");
                  // Reset local blob so openPdf triggers a fresh fetch
                  setPdfBlobKey(null);
                  setPdfPhase("idle");
                  // Kick a fresh pdf-status poll cycle
                  const poll = async (n) => {
                    if (n > 30) return;
                    try {
                      const { data } = await api.get(
                        `/workbench/drafts/${draft.id}/reports/${report.id}/pdf-status`,
                      );
                      setPdfServerCache(data?.state || "unknown");
                      if (data?.state === "ready") return;
                    } catch {
                      // ignore transient
                    }
                    setTimeout(() => poll(n + 1), 3000);
                  };
                  poll(0);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Regenerate failed.");
                  setPdfServerCache("stale");
                } finally {
                  setRegenerating(false);
                }
              }}
              disabled={regenerating || pdfPhase === "generating"}
              className="hv-btn hv-btn-ghost text-xs disabled:opacity-40"
              data-testid={`workbench-report-pdf-regenerate-${report.id}`}
              title="Force-rebuild the PDF from the current sections + attachments."
            >
              {regenerating
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
              Regenerate PDF
            </button>
          )}
          <button
            type="button"
            onClick={openPdf}
            disabled={isError || isRevising || pdfPhase === "generating"}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
            data-testid={`workbench-report-pdf-${report.id}`}
            title={pdfPhase === "ready" && pdfBlobKey === _cacheKey
              ? "Open the cached PDF in a new tab (instant)"
              : (pdfServerCache === "ready"
                ? "Open the pre-rendered PDF (server cache hit — instant)"
                : "Generate the final PDF and open it in a new tab")}
          >
            {pdfPhase === "generating"
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <FileText className="w-3 h-3" />}
            {pdfPhase === "generating" ? `Building… ${pdfElapsed}s` : "Export PDF"}
          </button>
          {/* [Iter 283] Download / Share / Send-to-customer buttons
              removed per user request — the new tab that opens with
              the PDF preview already exposes the browser's native
              download + share + print. Less is more. */}
          {confirmDelete ? (
            <span className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="hv-btn hv-btn-ghost text-xs text-rose-300"
                data-testid={`workbench-report-delete-confirm-${report.id}`}
              >
                Confirm delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-[10px] text-[#647087] hover:text-white px-1"
                data-testid={`workbench-report-delete-cancel-${report.id}`}
              >
                cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs text-[#647087] hover:text-rose-300 ml-auto"
              data-testid={`workbench-report-delete-${report.id}`}
              title="Delete report"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Iter 278 — Inline PDF status panel. Three states (idle hidden):
            generating: spinner + elapsed counter + Cancel
            ready:      "PDF ready (cached)" + Open / Download / Share
            error:      red panel with HTTP status + Try again + tech detail
          Auto-opens the PDF in a new tab on first success (Q1a). */}
      {pdfPhase === "generating" && (
        <div
          className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-3 py-2.5 flex items-center gap-3"
          data-testid={`workbench-report-pdf-generating-${report.id}`}
        >
          <Loader2 className="w-4 h-4 animate-spin text-cyan-300 shrink-0" />
          <div className="flex-1 text-[11px]">
            <div className="text-cyan-200 font-medium">
              Building your PDF… <span className="tabular-nums">{pdfElapsed}s</span>
            </div>
            <div className="text-[#647087] mt-0.5">
              Server can take up to 60 seconds on busy days. Your PDF will open in a new tab when it&rsquo;s ready.
            </div>
          </div>
          <button
            type="button"
            onClick={cancelPdf}
            className="text-[10px] uppercase tracking-wider text-[#647087] hover:text-rose-300 shrink-0"
            data-testid={`workbench-report-pdf-cancel-${report.id}`}
          >
            Cancel
          </button>
        </div>
      )}

      {pdfPhase === "ready" && pdfBlobKey === _cacheKey && (
        <div
          className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 flex items-center gap-3 text-[11px]"
          data-testid={`workbench-report-pdf-ready-${report.id}`}
        >
          <FileText className="w-4 h-4 text-emerald-300 shrink-0" />
          <div className="flex-1">
            <div className="text-emerald-200 font-medium">PDF ready · cached</div>
            <div className="text-[#647087] mt-0.5">
              Open, Download and Share are now instant. Refining the report will regenerate it.
            </div>
          </div>
          <button
            type="button"
            onClick={openPdf}
            className="text-[10px] uppercase tracking-wider text-emerald-200 hover:text-emerald-100 shrink-0"
            data-testid={`workbench-report-pdf-reopen-${report.id}`}
          >
            Open again
          </button>
        </div>
      )}

      {pdfPhase === "error" && pdfErr && (
        <div
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-3 flex flex-col gap-2 text-[11px]"
          data-testid={`workbench-report-pdf-error-${report.id}`}
        >
          <div className="flex items-start gap-2">
            <AlertOctagon className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-rose-200 font-medium">
                PDF generation failed
                {pdfErr.status ? <span className="text-rose-300/70 font-normal ml-1.5">(HTTP {pdfErr.status})</span> : null}
              </div>
              <div className="text-[#a0aabf] mt-0.5 leading-relaxed">
                {pdfErr.message} If this keeps happening, the server may be restarting or under load — try again in 30 seconds.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pl-6">
            <button
              type="button"
              onClick={openPdf}
              className="hv-btn hv-btn-primary text-[10px]"
              data-testid={`workbench-report-pdf-retry-${report.id}`}
            >
              <FileText className="w-3 h-3" /> Try again
            </button>
            <button
              type="button"
              onClick={() => setPdfShowDetail((v) => !v)}
              className="text-[10px] text-[#647087] hover:text-white ml-auto"
              data-testid={`workbench-report-pdf-detail-toggle-${report.id}`}
            >
              {pdfShowDetail ? "Hide" : "Show"} technical detail
            </button>
          </div>
          {pdfShowDetail && (
            <pre
              className="text-[9px] text-[#647087] bg-black/40 rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap break-all"
              data-testid={`workbench-report-pdf-detail-${report.id}`}
            >
{`status: ${pdfErr.status || "—"}
message: ${pdfErr.message || "—"}
body: ${(pdfErr.body || "").slice(0, 800) || "—"}`}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// Iter 427.p25 — Family Refresh bar (Phase C).
// Kicks off a non-destructive re-generation across every non-superseded
// report in the draft. Each report ends up with a `pending_refresh`
// payload the rep can accept or reject individually. The bar reports
// how many are running / awaiting review so the rep sees progress at
// a glance.
function FamilyRefreshBar({ draftId, reports, onFamilyChanged, onCombineOpen }) {
  const [busy, setBusy] = useState(false);
  const activeReady = reports.filter(
    (r) => r.status === "ready" && r.proposal_status !== "superseded",
  );
  const combineEligible = reports.filter(
    (r) => r.status === "ready" && (r.report_kind || "proposal") !== "comparison",
  );
  const pendingRunning = reports.filter(
    (r) => (r.pending_refresh || {}).status === "running",
  ).length;
  const pendingReady = reports.filter(
    (r) => (r.pending_refresh || {}).status === "ready",
  ).length;

  const kick = async () => {
    if (busy || activeReady.length === 0) return;
    if (!window.confirm(
      `Regenerate ${activeReady.length} proposal${activeReady.length === 1 ? "" : "s"} against the current Brainstorm state?\n\n`
      + "This is non-destructive — every proposal keeps its current body until you review the diff and Accept each one.",
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draftId}/reports/refresh-family`,
        {},
      );
      onFamilyChanged?.((data.items || [])
        .filter((x) => x.queued)
        .map((x) => ({ id: x.report_id, pending_refresh: { status: "running" } })));
      toast.success(
        data.count > 0
          ? `Refreshing ${data.count} proposal${data.count === 1 ? "" : "s"}… diffs will appear as each finishes.`
          : "Nothing to refresh right now.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Family refresh failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md border border-cyan-400/25 bg-cyan-500/[0.05] p-2.5 flex flex-wrap items-center gap-2"
      data-testid={`workbench-family-refresh-bar-${draftId}`}
    >
      <RotateCcw className={`w-3.5 h-3.5 text-cyan-200 ${pendingRunning > 0 ? "animate-spin" : ""}`} />
      <div className="text-[11px] text-[#cbd5e1] flex-1 min-w-[220px]">
        <span className="font-medium text-cyan-100">Family sync.</span>{" "}
        Fresh Brainstorm evidence? Regenerate every active proposal and review section-level diffs before promoting.
        {pendingRunning > 0 && (
          <span className="text-cyan-200"> · {pendingRunning} generating…</span>
        )}
        {pendingReady > 0 && (
          <span className="text-emerald-200"> · {pendingReady} awaiting review</span>
        )}
      </div>
      <button
        type="button"
        onClick={kick}
        disabled={busy || activeReady.length === 0}
        className="text-[11px] px-2.5 py-1 rounded border border-cyan-400/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-100 disabled:opacity-40 inline-flex items-center gap-1"
        data-testid={`workbench-family-refresh-btn-${draftId}`}
        title={
          activeReady.length === 0
            ? "No active proposals to refresh."
            : `Refresh ${activeReady.length} active proposal${activeReady.length === 1 ? "" : "s"}.`
        }
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
        Refresh all active ({activeReady.length})
      </button>
      <button
        type="button"
        onClick={onCombineOpen}
        disabled={combineEligible.length < 2}
        className="text-[11px] px-2.5 py-1 rounded border border-fuchsia-400/40 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-100 disabled:opacity-40 inline-flex items-center gap-1"
        data-testid={`workbench-combine-open-btn-${draftId}`}
        title={
          combineEligible.length < 2
            ? "Need at least 2 ready proposals to combine."
            : `Combine ${combineEligible.length} proposals into one comparison document.`
        }
      >
        <Copy className="w-3 h-3" />
        Combine variants ({combineEligible.length})
      </button>
    </div>
  );
}

// Iter 427.p25 — Pending refresh card (Phase C).
// Renders inside a ReportRow when the server has staged a
// non-destructive refresh. Shows section-level diff (added / removed
// / modified with word-count deltas) plus Accept / Reject buttons.
// Polls while `status === "running"` so the diff appears without
// requiring a manual page reload.
function PendingRefreshCard({ draftId, report, onChanged }) {
  const [busy, setBusy] = useState(false);
  const pending = report.pending_refresh || {};
  const status = pending.status;
  const diff = pending.section_diff || [];

  const runPoll = React.useRef(null);
  React.useEffect(() => {
    if (status !== "running") return undefined;
    const tick = async () => {
      try {
        const { data } = await api.get(
          `/workbench/drafts/${draftId}/reports/${report.id}`,
        );
        if (data) onChanged?.(data);
      } catch { /* silent */ }
    };
    runPoll.current = setInterval(tick, 4_000);
    return () => clearInterval(runPoll.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (!status) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/workbench/drafts/${draftId}/reports/${report.id}/refresh/accept`);
      toast.success("Refresh accepted — proposal updated.");
      const { data } = await api.get(`/workbench/drafts/${draftId}/reports/${report.id}`);
      onChanged?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed.");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/workbench/drafts/${draftId}/reports/${report.id}/refresh/reject`);
      toast.message("Discarded.");
      const { data } = await api.get(`/workbench/drafts/${draftId}/reports/${report.id}`);
      onChanged?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mx-3 mb-3 rounded-md border border-cyan-400/40 bg-cyan-500/[0.06] p-2.5 space-y-2"
      data-testid={`workbench-pending-refresh-${report.id}`}
    >
      <div className="flex items-center gap-2">
        <RotateCcw className={`w-3.5 h-3.5 text-cyan-200 ${status === "running" ? "animate-spin" : ""}`} />
        <div className="text-[11px] text-cyan-100 font-medium">
          {status === "running"
            ? "Regenerating against fresh brainstorm…"
            : "Refresh ready — review before promoting"}
        </div>
        {status === "ready" && (
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={accept}
              disabled={busy}
              data-testid={`workbench-pending-refresh-accept-${report.id}`}
              className="text-[10px] px-2 py-0.5 rounded border border-emerald-400/50 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100 disabled:opacity-40 inline-flex items-center gap-1"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              Accept
            </button>
            <button
              type="button"
              onClick={reject}
              disabled={busy}
              data-testid={`workbench-pending-refresh-reject-${report.id}`}
              className="text-[10px] px-2 py-0.5 rounded border border-white/15 bg-black/20 hover:bg-black/40 text-[#a0aabf] disabled:opacity-40 inline-flex items-center gap-1"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
              Discard
            </button>
          </div>
        )}
      </div>
      {status === "ready" && diff.length > 0 && (
        <ul className="space-y-0.5 text-[11px] pl-1">
          {diff.map((d, i) => {
            const badge =
              d.type === "added" ? "bg-emerald-500/15 text-emerald-100 border-emerald-400/40"
              : d.type === "removed" ? "bg-rose-500/15 text-rose-100 border-rose-400/40"
              : d.type === "modified" ? "bg-amber-500/15 text-amber-100 border-amber-400/40"
              : "bg-white/5 text-[#a0aabf] border-white/10";
            return (
              <li
                key={i}
                className="flex items-center gap-2"
                data-testid={`workbench-pending-refresh-diff-${d.type}-${i}`}
              >
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge}`}>
                  {d.type}
                </span>
                <span className="text-[#cbd5e1] truncate">{d.heading || "(intro)"}</span>
                {d.type === "modified" && typeof d.delta_words === "number" && (
                  <span className={d.delta_words > 0 ? "text-emerald-200/80" : "text-rose-200/80"}>
                    {d.delta_words > 0 ? "+" : ""}{d.delta_words} words
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// Iter 427.p32 — Per-report Appendix Overrides panel.
// Same-family variants share a session/draft attachment pool, so
// their Context/Inline/Appendix inclusion used to be one setting
// for ALL proposals. This panel lets the rep set a per-report
// override on any single upload — V1 keeps QTE-A as its appendix,
// V2 gets QTE-B, V3 gets QTE-C, all from the same pool.
function ReportAppendixOverridesPanel({ draftId, report }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [savingUid, setSavingUid] = useState(null);
  const [locking, setLocking] = useState(false);
  const [targetQuote, setTargetQuote] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/workbench/drafts/${draftId}/reports/${report.id}/attachments-view`,
      );
      setItems(data?.items || []);
      setTargetQuote(data?.target_quote_number || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load attachments.");
    } finally {
      setLoading(false);
    }
  }, [draftId, report.id]);

  React.useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const setInclusion = async (upload_id, inclusion) => {
    setSavingUid(upload_id);
    try {
      await api.patch(
        `/workbench/drafts/${draftId}/reports/${report.id}/attachments/${upload_id}`,
        { inclusion: inclusion || null },
      );
      await load();
      toast.success(
        inclusion
          ? `Set to ${inclusion} for this proposal only.`
          : "Cleared — back to the session-wide default.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSavingUid(null);
    }
  };

  const lockToPricingSource = async () => {
    setLocking(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draftId}/reports/${report.id}/attachments/lock-to-pricing-source`,
      );
      toast.success(
        `Locked appendix to ${data.quote_number.toUpperCase()} — ${data.matched} file(s) matched.`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lock failed.");
    } finally {
      setLocking(false);
    }
  };

  const filtered = q.trim()
    ? items.filter((it) => (
        (it.filename || "").toLowerCase().includes(q.toLowerCase())
        || (it.caption || "").toLowerCase().includes(q.toLowerCase())
      ))
    : items;

  const overrideCount = items.filter((it) => it.inclusion_source === "override").length;
  const appendixCount = items.filter((it) => it.effective_inclusion === "appendix").length;

  return (
    <div
      className="mx-3 mb-3 rounded-md border border-white/10 bg-white/[0.03]"
      data-testid={`workbench-report-appendix-panel-${report.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`workbench-report-appendix-toggle-${report.id}`}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-[#cbd5e1] hover:bg-white/[0.03] rounded-md"
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        <FileText className="w-3 h-3 text-cyan-300" />
        <span className="font-medium">Attachments for this proposal</span>
        <span className="text-[10px] text-[#647087]">
          · {appendixCount} appendix{appendixCount === 1 ? "" : "es"}
          {overrideCount > 0 && (
            <span className="text-emerald-300 ml-1">
              · {overrideCount} override{overrideCount === 1 ? "" : "s"}
            </span>
          )}
        </span>
      </button>
      {open && (
        <div className="px-2.5 pb-2.5 pt-1 space-y-2 border-t border-white/5">
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by filename…"
              data-testid={`workbench-report-appendix-search-${report.id}`}
              className="flex-1 bg-black/25 rounded px-2 py-1 text-[10px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-cyan-400/40 border border-white/10"
            />
            {targetQuote && (
              <button
                type="button"
                onClick={lockToPricingSource}
                disabled={locking}
                data-testid={`workbench-report-appendix-lock-${report.id}`}
                title={`Lock the appendix to the pricing source quote (${targetQuote.toUpperCase()}) — everything else becomes context.`}
                className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-100 inline-flex items-center gap-1"
              >
                {locking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Lock to {targetQuote.toUpperCase()}
              </button>
            )}
          </div>
          {loading && <div className="text-[10px] text-[#647087]">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-[10px] text-[#647087] italic">
              {q.trim() ? "No matches." : "No attachments uploaded yet — drop a PDF or image into the chat first."}
            </div>
          )}
          {filtered.map((it) => (
            <ReportAppendixRow
              key={`${it.source}-${it.upload_id}`}
              row={it}
              busy={savingUid === it.upload_id}
              onChange={(inclusion) => setInclusion(it.upload_id, inclusion)}
              testIdBase={`workbench-report-appendix-row-${report.id}`}
            />
          ))}
          {overrideCount > 0 && (
            <div className="pt-1 border-t border-white/5 text-[10px] text-[#647087] leading-snug">
              This proposal has {overrideCount} attachment override(s). Other variants in the family are unaffected.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportAppendixRow({ row, busy, onChange, testIdBase }) {
  const TIERS = ["context", "inline", "appendix"];
  const tierLabel = { context: "Context", inline: "Inline", appendix: "Appendix" };
  const tierBg = {
    context: "bg-white/5 text-[#a0aabf] border-white/10",
    inline: "bg-amber-500/15 text-amber-100 border-amber-400/40",
    appendix: "bg-cyan-500/15 text-cyan-100 border-cyan-400/40",
  };
  const isOverride = row.inclusion_source === "override";
  return (
    <div
      className={`rounded border ${row.suggested_appendix ? "border-emerald-400/40 bg-emerald-500/[0.04]" : "border-white/10 bg-black/20"} p-1.5 space-y-1`}
      data-testid={`${testIdBase}-${row.upload_id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[9px] uppercase tracking-wider text-[#647087] shrink-0">
          {row.kind === "file" ? "PDF" : row.kind === "image" ? "IMG" : row.kind.toUpperCase()}
        </span>
        <span className="text-[11px] text-[#cbd5e1] truncate flex-1" title={row.filename}>
          {row.filename || "(unnamed)"}
        </span>
        {row.suggested_appendix && (
          <span
            className="text-[9px] px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-100 border border-emerald-400/40 shrink-0"
            title="This file mentions the report's pricing-source quote number."
          >
            match
          </span>
        )}
        {isOverride && (
          <span
            className="text-[9px] text-emerald-300 shrink-0"
            title="This report overrides the session-wide default."
          >
            ●
          </span>
        )}
      </div>
      {row.caption && (
        <div className="text-[10px] text-[#647087] italic truncate" title={row.caption}>
          {row.caption}
        </div>
      )}
      <div className="flex items-center gap-1 flex-wrap">
        {TIERS.map((tier) => {
          const active = row.effective_inclusion === tier;
          return (
            <button
              key={tier}
              type="button"
              onClick={() => onChange(active && isOverride ? null : tier)}
              disabled={busy}
              data-testid={`${testIdBase}-${row.upload_id}-${tier}`}
              title={
                active && isOverride
                  ? `Currently ${tier} (override). Click to remove override and inherit the session-wide default (${row.global_inclusion}).`
                  : `Set this file to ${tier} for this proposal only.`
              }
              className={`text-[10px] px-1.5 py-0.5 rounded border ${
                active ? tierBg[tier] : "bg-white/5 text-[#647087] border-white/10 hover:bg-white/10"
              } disabled:opacity-40`}
            >
              {tierLabel[tier]}
              {active && isOverride && " ✕"}
            </button>
          );
        })}
        <span className="text-[9px] text-[#647087] ml-auto">
          global: {row.global_inclusion || "context"}
        </span>
      </div>
    </div>
  );
}



// Iter 427.p26 — Combine Variants modal (Phase D).
// Rep picks 2+ source proposals and a layout, submits, gets a new
// comparison report in the draft. Blocks until the LLM finishes
// (backend runs synchronously; comparison composition is small
// relative to a full generation because source bodies are already
// written).
function CombineVariantsModal({ draftId, reports, onDone, onClose }) {
  const eligible = reports.filter(
    (r) => r.status === "ready" && (r.report_kind || "proposal") !== "comparison",
  );
  const [selected, setSelected] = React.useState(() =>
    eligible.slice(0, 2).map((r) => r.id),
  );
  const [layout, setLayout] = React.useState("matrix");
  const [label, setLabel] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const move = (id, dir) => {
    // Ordering matters for LINEAR (first = primary).
    setSelected((prev) => {
      const i = prev.indexOf(id);
      if (i < 0) return prev;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const submit = async () => {
    if (busy) return;
    if (selected.length < 2) {
      toast.error("Pick at least 2 proposals to combine.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draftId}/reports/combine`,
        {
          source_ids: selected,
          layout,
          variant_label: label.trim() || undefined,
        },
      );
      toast.success("Combined proposal generated.");
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Combine failed.");
    } finally {
      setBusy(false);
    }
  };

  const layoutHelp = {
    matrix: "Row per section, column per option, cell = the option's take on that section.",
    stacked: "Section by section, each option renders its own H3 block, then a Differentiator paragraph.",
    linear: "The first option is the main body. Other options appear as callouts under each of its sections.",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid={`workbench-combine-modal-${draftId}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="max-w-2xl w-full rounded-lg border border-cyan-400/40 bg-[#0a1520] p-4 space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <Copy className="w-4 h-4 text-cyan-200" />
          <div className="text-sm font-medium text-[#e0e7f0]">Combine variants into ONE comparison proposal</div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto p-1 rounded hover:bg-white/10 text-[#a0aabf]"
            data-testid={`workbench-combine-close-${draftId}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">1 · Pick options to include (order matters for Linear — first = primary)</div>
          <div className="rounded-md border border-white/10 bg-black/25 divide-y divide-white/5">
            {eligible.length === 0 && (
              <div className="p-3 text-[11px] text-[#647087]">Nothing eligible — combine needs at least 2 ready non-comparison reports.</div>
            )}
            {eligible.map((r) => {
              const checked = selected.includes(r.id);
              const pos = selected.indexOf(r.id);
              return (
                <label
                  key={r.id}
                  className={`flex items-center gap-2 p-2 text-[11px] cursor-pointer ${checked ? "bg-cyan-500/[0.06]" : "hover:bg-white/[0.03]"}`}
                  data-testid={`workbench-combine-source-${r.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(r.id)}
                    className="accent-cyan-400"
                    data-testid={`workbench-combine-source-check-${r.id}`}
                  />
                  <span className="text-[#cbd5e1] flex-1 truncate">
                    {r.variant_label || r.headline || r.name || r.id}
                    {r.variant_note && <span className="text-[#647087]"> — {r.variant_note}</span>}
                  </span>
                  {checked && (
                    <>
                      <span className="text-[9px] uppercase tracking-wider text-cyan-200">#{pos + 1}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); move(r.id, "up"); }}
                        disabled={pos === 0}
                        className="p-0.5 rounded hover:bg-white/10 text-[#a0aabf] disabled:opacity-25"
                        data-testid={`workbench-combine-source-up-${r.id}`}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); move(r.id, "down"); }}
                        disabled={pos === selected.length - 1}
                        className="p-0.5 rounded hover:bg-white/10 text-[#a0aabf] disabled:opacity-25"
                        data-testid={`workbench-combine-source-down-${r.id}`}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">2 · Pick a layout</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
            {["matrix", "stacked", "linear"].map((L) => (
              <button
                key={L}
                type="button"
                onClick={() => setLayout(L)}
                data-testid={`workbench-combine-layout-${L}`}
                className={`text-left rounded-md border p-2 text-[11px] transition ${
                  layout === L
                    ? "border-cyan-400/60 bg-cyan-500/[0.08] text-cyan-100"
                    : "border-white/10 bg-black/20 text-[#a0aabf] hover:border-white/20"
                }`}
              >
                <div className="font-medium uppercase tracking-wider text-[10px] mb-0.5">{L}</div>
                <div className="text-[10px] opacity-80">{layoutHelp[L]}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">3 · Optional label</div>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={`e.g. "${eligible.slice(0, 3).map((r) => r.variant_label || "?").join(" vs ")}"`}
            disabled={busy}
            data-testid={`workbench-combine-label-${draftId}`}
            className="w-full bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-cyan-400/30 border border-white/10"
          />
        </div>

        <div className="flex items-center gap-1.5 pt-1">
          <button
            type="button"
            onClick={submit}
            disabled={busy || selected.length < 2}
            data-testid={`workbench-combine-submit-${draftId}`}
            className="text-[11px] px-2.5 py-1 rounded border border-cyan-400/50 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-100 disabled:opacity-40 inline-flex items-center gap-1"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
            {busy ? "Composing…" : `Combine ${selected.length}`}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[11px] px-2.5 py-1 rounded border border-white/15 bg-black/20 hover:bg-black/40 text-[#a0aabf] disabled:opacity-40"
            data-testid={`workbench-combine-cancel-${draftId}`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Iter 427.p27 — Family Hub card (Phase E).
// A compact top-of-panel table showing every proposal in the draft
// side-by-side: status pill, variant label, pricing source chip,
// last-refreshed timestamp, sibling count, staleness badge, and
// one-tap actions (View / Refresh / Mark Official / Fork / Combine).
// Click a row → smooth-scroll to the matching ReportRow below.
function FamilyHubCard({ draftId, reports, brainstormLastAt, customerLinked, onCombineOpen, onReportsChanged }) {
  if (!reports || reports.length === 0) return null;
  const bs = brainstormLastAt ? new Date(brainstormLastAt).getTime() : 0;
  const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
      const dt = new Date(iso);
      const diffH = (Date.now() - dt.getTime()) / 3_600_000;
      if (diffH < 1) return `${Math.max(1, Math.floor(diffH * 60))}m ago`;
      if (diffH < 48) return `${Math.floor(diffH)}h ago`;
      return `${Math.floor(diffH / 24)}d ago`;
    } catch { return "—"; }
  };
  const scrollToRow = (rid) => {
    const el = document.querySelector(`[data-testid="workbench-report-identity-${rid}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const refreshOne = async (rid) => {
    try {
      await api.post(`/workbench/drafts/${draftId}/reports/${rid}/refresh`);
      onReportsChanged?.([{ id: rid, pending_refresh: { status: "running" } }]);
      toast.success("Refresh queued — the diff appears below when it lands.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed.");
    }
  };
  const markOfficial = async (rid) => {
    if (!customerLinked) {
      toast.error("Link a customer to the workbench first.");
      return;
    }
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draftId}/reports/${rid}/mark-official`, {},
      );
      onReportsChanged?.([{
        id: rid,
        proposal_status: "official",
        official_document_id: data.customer_document_id,
        official_customer_id: data.customer_id,
        official_project_id: data.project_id,
      }]);
      toast.success(data.project_id ? "Marked Official — sent to Customer docs and Project → Proposals." : "Marked Official — sent to Customer docs.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark Official failed.");
    }
  };
  const pricingLabel = (r) => {
    const ps = r.pricing_source;
    if (!ps || !ps.type) return null;
    if (ps.type === "quote") return `Quote ${ps.quote_number || ps.quote_id?.slice(-6)}`;
    if (ps.type === "intake") return "Intake JSON";
    return ps.type;
  };
  return (
    <div
      className="rounded-md border border-white/10 bg-black/25 p-2.5 space-y-2"
      data-testid={`workbench-family-hub-${draftId}`}
    >
      <div className="flex items-center gap-2 text-[11px] text-cyan-100">
        <Users className="w-3.5 h-3.5 text-cyan-200" />
        <span className="font-medium">Proposal family</span>
        <span className="text-[#647087]">· {reports.length} variant{reports.length === 1 ? "" : "s"}</span>
        {brainstormLastAt && (
          <span className="text-[#647087] ml-auto">
            Brainstorm last updated {fmtWhen(brainstormLastAt)}
          </span>
        )}
      </div>
      <div className="rounded border border-white/10 divide-y divide-white/5 overflow-hidden">
        {reports.map((r) => {
          const kind = r.report_kind || "proposal";
          const status = r.proposal_status || (kind === "comparison" ? "comparison" : "draft");
          const isOfficial = status === "official";
          const isComparison = kind === "comparison";
          const isSuperseded = status === "superseded";
          const stale = bs && r.status === "ready" && !isComparison && (() => {
            const stamp = r.last_refreshed_at || r.ready_at || r.updated_at || r.created_at;
            if (!stamp) return false;
            return new Date(stamp).getTime() < bs;
          })();
          const pendingStatus = (r.pending_refresh || {}).status;
          return (
            <div
              key={r.id}
              className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-[11px] hover:bg-white/[0.03]"
              data-testid={`workbench-family-hub-row-${r.id}`}
            >
              <span
                className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0 ${
                  isComparison ? "bg-fuchsia-500/15 border-fuchsia-400/50 text-fuchsia-100"
                  : isOfficial ? "bg-emerald-500/15 border-emerald-400/50 text-emerald-100"
                  : isSuperseded ? "bg-white/5 border-white/15 text-[#647087] line-through"
                  : "bg-white/5 border-white/15 text-[#a0aabf]"
                }`}
                data-testid={`workbench-family-hub-status-${r.id}`}
              >
                {isComparison ? "🧩 Comparison" : isOfficial ? "✓ Official" : status}
              </span>
              <button
                type="button"
                onClick={() => scrollToRow(r.id)}
                className="text-[#cbd5e1] hover:text-cyan-100 underline-offset-2 hover:underline text-left flex-1 min-w-[160px] truncate"
                data-testid={`workbench-family-hub-jump-${r.id}`}
                title="Scroll to this report"
              >
                {r.variant_label || r.headline || r.name || r.id}
              </button>
              {pricingLabel(r) && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded border border-amber-400/30 bg-amber-500/[0.08] text-amber-100 shrink-0"
                  data-testid={`workbench-family-hub-pricing-${r.id}`}
                >
                  {pricingLabel(r)}
                </span>
              )}
              {r.sibling_ids?.length > 0 && (
                <span className="text-[10px] text-[#647087] shrink-0">
                  · {r.sibling_ids.length} sibling{r.sibling_ids.length === 1 ? "" : "s"}
                </span>
              )}
              {stale && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-500/[0.12] text-amber-100 shrink-0"
                  data-testid={`workbench-family-hub-stale-${r.id}`}
                  title="Brainstorm has new evidence since this proposal was last generated — refresh recommended."
                >
                  Stale
                </span>
              )}
              {pendingStatus && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${
                    pendingStatus === "running"
                      ? "bg-cyan-500/[0.12] border-cyan-400/40 text-cyan-100"
                      : "bg-emerald-500/[0.15] border-emerald-400/40 text-emerald-100"
                  }`}
                  data-testid={`workbench-family-hub-pending-${r.id}`}
                >
                  {pendingStatus === "running" ? "Regenerating…" : "Refresh ready"}
                </span>
              )}
              <span className="text-[10px] text-[#647087] shrink-0">
                {fmtWhen(r.last_refreshed_at || r.ready_at || r.updated_at || r.created_at)}
              </span>
              <div className="flex items-center gap-0.5 shrink-0 ml-auto">
                {r.status === "ready" && !pendingStatus && !isComparison && (
                  <button
                    type="button"
                    onClick={() => refreshOne(r.id)}
                    className="p-1 rounded hover:bg-cyan-500/15 text-cyan-200"
                    data-testid={`workbench-family-hub-refresh-${r.id}`}
                    title="Regenerate against fresh Brainstorm — non-destructive."
                  >
                    <RotateCcw className="w-3 h-3" />
                  </button>
                )}
                {!isOfficial && !isComparison && r.status === "ready" && (
                  <button
                    type="button"
                    onClick={() => markOfficial(r.id)}
                    disabled={!customerLinked}
                    className="p-1 rounded hover:bg-emerald-500/15 text-emerald-200 disabled:opacity-30"
                    data-testid={`workbench-family-hub-mark-${r.id}`}
                    title={customerLinked ? "Mark this proposal Official → sends to Customer Docs." : "Link a customer first."}
                  >
                    <CheckCircle2 className="w-3 h-3" />
                  </button>
                )}
                {r.status === "ready" && !isComparison && (
                  <button
                    type="button"
                    onClick={() => scrollToRow(r.id)}
                    className="p-1 rounded hover:bg-fuchsia-500/15 text-fuchsia-200"
                    data-testid={`workbench-family-hub-fork-${r.id}`}
                    title="Scroll down to open the Fork panel on this proposal."
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {reports.filter((r) => (r.report_kind || "proposal") !== "comparison" && r.status === "ready").length >= 2 && (
        <div className="flex items-center gap-2 text-[10px] text-[#647087]">
          <span>Compare options side-by-side:</span>
          <button
            type="button"
            onClick={onCombineOpen}
            className="text-fuchsia-200 hover:text-fuchsia-100 underline-offset-2 hover:underline"
            data-testid={`workbench-family-hub-combine-${draftId}`}
          >
            open Combine variants →
          </button>
        </div>
      )}
    </div>
  );
}

function ReportsPanel({ draft }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [audience, setAudience] = useState("customer");
  // Iter 427.p26 — Combine modal state.
  const [combineOpen, setCombineOpen] = useState(false);
  // Iter 290zp.11 (Stage A) — Per-report directive brief. Free-form
  // textarea where the rep types the structural guidance for THIS
  // report ("open with exec summary stating problem, then numbered
  // stages 1-9, container vs in-building as table, finish with
  // single recommendation"). Sent as `guide_prompt` on /reports POST
  // and stored on the report doc. Persistence is per-report — each
  // new audience/length click creates a new report with its own
  // brief, matching the existing refinement/version-history model.
  const [guidePrompt, setGuidePrompt] = useState("");
  // [Iter 290zp.13] Per-report customisation. These three together
  // give the rep full control of the H1 (no more hardcoded "—
  // Pre-sales advisory report"), and let them gate which sections
  // the LLM is allowed to emit (sectionsToEmit is a future-iter
  // checklist; for now we ship a free-text comma-list).
  const [customerName, setCustomerName] = useState("");
  const [reportTypeLabel, setReportTypeLabel] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workbench/drafts/${draft.id}/reports`);
      const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      setReports(list);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [draft.id]);

  useEffect(() => { load(); }, [load]);

  const generate = async (length) => {
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/workbench/drafts/${draft.id}/reports`,
        // Iter 239 — explicitly tag the current stage so the rep gets
        // a stage-appropriate report (the backend defaults to
        // draft.current_stage anyway, but being explicit avoids
        // surprises if they switch stage mid-generation).
        // Iter 290zp.11 — also pass the directive brief (trimmed),
        // which the LLM treats as BINDING for section ordering and
        // framing on this report.
        {
          length,
          audience,
          stage: draft.current_stage,
          guide_prompt: (guidePrompt || "").trim() || null,
          customer_name: (customerName || "").trim() || null,
          report_type_label: (reportTypeLabel || "").trim() || null,
        },
      );
      setReports((prev) => [data, ...prev]);
      const audLabel = AUDIENCE_OPTIONS.find((a) => a.key === audience)?.label || audience;
      toast.success(`Generating ${audLabel.toLowerCase()} ${length} report — refreshing automatically.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start report generation.");
    } finally {
      setGenerating(false);
    }
  };

  const handleChanged = useCallback((updated) => {
    // Iter 427.p22 — some children (ReportSectionTabs) call
    // onChanged?.() with no arg after a save-in-place mutation
    // (heading/body PATCH). Nothing needs to change in this list
    // in that case; just no-op instead of crashing on updated.id.
    if (!updated || !updated.id) return;
    setReports((prev) => {
      if (updated._deleted) return prev.filter((r) => r.id !== updated.id);
      const exists = prev.some((r) => r.id === updated.id);
      if (!exists) {
        // Iter 427.p24 — fork inserts a brand-new report; prepend so
        // the newest variant appears at the top of the list.
        return [updated, ...prev];
      }
      return prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r));
    });
  }, []);

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 sm:p-4 space-y-3" data-testid="workbench-reports-panel">
      <header className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-cyan-300" />
        <h2 className="text-sm font-semibold">Engineering reports</h2>
        <span className="text-[10px] text-[#647087]">
          Draft text first · refine with Hidro · export PDF when ready
        </span>
      </header>

      {/* Iter 238.1 — audience picker. Each audience renders a completely
          different style of document (steps vs math vs plain language).
          Default = customer because "send to customer" is the most common
          downstream action; the rep can flip to technician/engineering
          before clicking a length button. */}
      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">
          1 · Pick the audience
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="workbench-report-audience-picker">
          {AUDIENCE_OPTIONS.map((opt) => {
            const active = audience === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setAudience(opt.key)}
                className={`text-left rounded-md border px-3 py-2 transition ${
                  active
                    ? "border-cyan-400/60 bg-cyan-500/10 ring-1 ring-cyan-400/40"
                    : "border-white/10 bg-white/[0.02] hover:border-white/25"
                }`}
                data-testid={`workbench-audience-${opt.key}`}
                aria-pressed={active}
              >
                <div className={`text-xs font-semibold ${active ? "text-cyan-100" : "text-[#cbd5e1]"}`}>
                  {opt.label}
                </div>
                <div className="text-[10px] text-[#647087] mt-0.5 leading-tight">
                  {opt.hint}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">
          1.5 · Title fields
          <span className="ml-1 normal-case tracking-normal text-[10px] text-[#647087]/80">
            (drives the H1 on every page of the PDF)
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            disabled={generating}
            maxLength={120}
            placeholder="Customer / site name (e.g. Nkwe Pleasure Resort)"
            className="hv-input w-full text-[12px]"
            data-testid="workbench-report-customer-name"
          />
          <input
            type="text"
            value={reportTypeLabel}
            onChange={(e) => setReportTypeLabel(e.target.value)}
            disabled={generating}
            maxLength={120}
            placeholder="Report type (e.g. System replacement proposal · Engineering assessment · Pre-sales report)"
            className="hv-input w-full text-[12px]"
            data-testid="workbench-report-type-label"
          />
        </div>
        <p className="text-[10px] text-[#647087]">
          H1 becomes <code className="text-[#a0aabf]">{(customerName || "{customer}").trim()}{reportTypeLabel ? ` — ${reportTypeLabel.trim()}` : ""}</code>.
          Leave a field blank to drop that part of the title.
        </p>
      </div>

      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">
          2 · Brief for this report
          <span className="ml-1 normal-case tracking-normal text-[10px] text-[#647087]/80">
            (optional — tell Hidro how to structure this report)
          </span>
        </div>
        <DictTextarea
          value={guidePrompt}
          onChange={(e) => setGuidePrompt(e.target.value)}
          disabled={generating}
          rows={5}
          maxLength={8000}
          placeholder={[
            "Open with an executive summary stating the problem: current plant is operational but aged, reaching end of life with recurring failures.",
            "",
            "Then propose the new system as 9 numbered stages (raw circulation+ozone → AFM → bag filter → UF → buffer → antiscalant → biofouling → RO → clean-water circulation+ozone).",
            "",
            "Frame the softener removal as 'on review of 5-year evidence, the better decision is to upsize UF instead' — not as a justification section at the back.",
            "",
            "Include a Capacity & water-quality targets section: 45 000 L/day demand, 3 m³/h permeate, wastewater drops from 5 m³/h to 2-3 m³/h, permeate TDS 10-50, blended TDS 100-200.",
            "",
            "Contrast container vs in-building delivery as a table, recommend the container option (zero downtime, existing plant becomes serviced standby).",
            "",
            "Finish with a single clear recommendation and a Glossary of technical terms.",
          ].join("\n")}
          className="hv-input w-full text-[12px] leading-snug resize-y"
          data-testid="workbench-report-guide-prompt"
        />
        <p className="text-[10px] text-[#647087]">
          Each Generate click creates a new report version. The brief above is saved on that report;
          you can change it for the next version without losing this one.
        </p>
      </div>

      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">
          3 · Pick the length to generate
        </div>
        <div className="flex flex-wrap gap-2" data-testid="workbench-report-generate-actions">
          {LENGTH_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => generate(opt.key)}
              disabled={generating}
              className="hv-btn hv-btn-ghost text-xs flex-col items-start gap-0 px-3 py-2 text-left disabled:opacity-40"
              data-testid={`workbench-generate-${opt.key}`}
            >
              <span className="text-xs font-semibold text-cyan-200 flex items-center gap-1">
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                {opt.label}
              </span>
              <span className="text-[10px] text-[#647087] normal-case font-normal">{opt.hint}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Iter 290zp.12 (Stage B) — Proposal-only attachments uploader.
          Files attached here go straight into the customer PDF without
          appearing in the brainstorm chat. Wrapped in the
          WORKBENCH_SPLIT_UI feature flag so the legacy chat-only flow
          can be restored by setting REACT_APP_WORKBENCH_SPLIT_UI=0. */}
      {WORKBENCH_SPLIT_UI && (
        <ProposalAttachmentsUploader draftId={draft.id} />
      )}

      {!draft.customer_id && (
        <div className="text-[11px] text-amber-200/80 flex items-center gap-1.5" data-testid="workbench-reports-no-customer">
          <AlertTriangle className="w-3 h-3" />
          Link a customer to attach this draft to a real project.
        </div>
      )}

      {reports.length > 0 && (
        <FamilyHubCard
          draftId={draft.id}
          reports={reports}
          brainstormLastAt={draft.brainstorm_last_at}
          customerLinked={!!draft.customer_id}
          onCombineOpen={() => setCombineOpen(true)}
          onReportsChanged={(updates) => {
            setReports((prev) => prev.map((r) => {
              const u = updates.find((x) => x.id === r.id);
              return u ? { ...r, ...u } : r;
            }));
          }}
        />
      )}

      {reports.length > 0 && (
        <FamilyRefreshBar
          draftId={draft.id}
          reports={reports}
          onFamilyChanged={(updates) => {
            // Bulk state merge from the refresh-family response.
            setReports((prev) => prev.map((r) => {
              const u = updates.find((x) => x.id === r.id);
              return u ? { ...r, ...u } : r;
            }));
          }}
          onCombineOpen={() => setCombineOpen(true)}
        />
      )}
      {combineOpen && (
        <CombineVariantsModal
          draftId={draft.id}
          reports={reports}
          onClose={() => setCombineOpen(false)}
          onDone={(newReport) => {
            setCombineOpen(false);
            setReports((prev) => [newReport, ...prev]);
          }}
        />
      )}

      {loading ? (
        <div className="text-xs text-[#647087]">Loading reports…</div>
      ) : reports.length === 0 ? (
        <p className="text-xs text-[#647087]">
          No reports yet. Pick a length above — Hidro will draft a full engineering report from this brainstorm transcript.
        </p>
      ) : (
        <div className="space-y-2" data-testid="workbench-reports-list">
          {reports.map((r) => (
            <ReportRow key={r.id} draft={draft} report={r} onChanged={handleChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectWorkbenchDetail() {
  const { did } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workbench/drafts/${did}`);
      setDraft(data);
      setName(data?.name || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft not found.");
      navigate("/projects/workbench");
    } finally {
      setLoading(false);
    }
  }, [did, navigate]);

  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    if (!name.trim() || name === draft.name) {
      setEditingName(false);
      return;
    }
    try {
      await api.patch(`/workbench/drafts/${did}`, { name: name.trim() });
      setEditingName(false);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Rename failed.");
    }
  };

  const promote = async (stage) => {
    setBusy(true);
    try {
      await api.post(`/workbench/drafts/${did}/promote/${stage}`);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Promote failed.");
    } finally {
      setBusy(false);
    }
  };

  const back = async (stage) => {
    setBusy(true);
    try {
      await api.post(`/workbench/drafts/${did}/back-to-${stage}`);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't move back.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="text-sm text-[#647087]">Loading workbench draft…</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4" data-testid="workbench-detail">
      <div className="flex items-center gap-2">
        <Link to="/projects/workbench" className="hv-btn hv-btn-ghost text-xs" data-testid="workbench-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Workbench
        </Link>
        {draft.status === "archived" && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300">
            <AlertTriangle className="w-3 h-3" /> archived
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {editingName ? (
          <>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
              className="hv-input text-lg font-semibold flex-1"
              data-testid="workbench-name-input"
            />
            <button onClick={saveName} className="hv-btn hv-btn-ghost text-xs" data-testid="workbench-name-save">
              <Save className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight truncate" data-testid="workbench-name">
              {draft.name}
            </h1>
            <button
              onClick={() => setEditingName(true)}
              className="text-[#647087] hover:text-cyan-300 p-1"
              data-testid="workbench-name-edit"
              title="Rename"
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>

      <StageStrip draft={draft} onPromote={promote} onBack={back} busy={busy} />

      {/* Iter 239 — Stage-aware chat. Each stage has its own session
          and Hidro persona. Brainstorm = explore; Plan = inventory
          deep-dive; Estimate = priced cross-reference. */}
      {draft.current_stage === "brainstorm" && (
        <StageChat
          sessionId={draft.brainstorm_session_id || draft.engineering_session_id}
          customerId={draft.customer_id}
          stage="brainstorm"
          draftId={draft.id}
          kind={draft.kind}
          initialPrompt={location.state?.presetPrompt}
        />
      )}
      {draft.current_stage === "plan" && draft.plan_session_id && (
        <StageChat
          sessionId={draft.plan_session_id}
          customerId={draft.customer_id}
          stage="plan"
        />
      )}
      {draft.current_stage === "estimate" && draft.estimate_session_id && (
        <StageChat
          sessionId={draft.estimate_session_id}
          customerId={draft.customer_id}
          stage="estimate"
        />
      )}

      {/* Iter 247 — Persistent Bill of Materials across all stages.
          Auto-updates in the background as the rep chats; manually
          editable; estimate stage surfaces a "Search inventory"
          affordance to resolve non-stock items. */}
      <BomPanel draft={draft} />

      {/* Iter 290zp.15z02 — System map panel renders the AI-built
          engineering ledger (process chain + decisions + open questions)
          so the rep can see Hidro's current understanding at a glance
          without scrolling through chat history. */}
      <SystemMapPanel draft={draft} />

      <ReportsPanel draft={draft} />

      {(draft.current_stage === "plan" || draft.plan_id) && draft.current_stage !== "brainstorm" && (
        <PlanPanel draft={draft} />
      )}

      {(draft.current_stage === "estimate" || draft.estimate_id) && draft.current_stage === "estimate" && (
        <EstimatePanel draft={draft} />
      )}

      <div className="flex flex-wrap items-start gap-2" data-testid="workbench-project-actions">
        <PromoteToProjectPanel draft={draft} onPromoted={() => load()} />
        <LinkToProjectPanel draft={draft} onLinked={() => load()} />
      </div>
    </div>
  );
}

/* ─────────────── Iter 427.p50 — Image section editor ─────────────────
 * Live preview + caption + width preset controls for a `kind:"image"`
 * section. Persists via the shared `patchSection()` PATCH.

/* ─────────────── Iter 427.p51 — Drag-reorder for report sections ─────
 * `SortableSectionCard` is a thin wrapper around the section render.
 * The grip icon is the ONLY drag activator (attributes + listeners are
 * only spread on it), so users can freely click into the textarea /
 * heading / refine input without triggering a drag. 5-pixel activation
 * distance on the PointerSensor guards against accidental drags on
 * fast clicks.
 * ---------------------------------------------------------------- */
function PageBreakMarker({ id }) {
  return (
    <div className="flex items-center gap-2 my-2 text-[9px] uppercase tracking-[0.2em] text-cyan-300/70" data-testid={`workbench-page-break-${id}`}>
      <span className="flex-1 border-t border-dashed border-cyan-400/40" />
      New page
      <span className="flex-1 border-t border-dashed border-cyan-400/40" />
    </div>
  );
}

function SortableSectionCard({
  sec, idx, total, sections, addSection, addImageSection, importPdfSections,
  deleteSection, patchSection, refineSection,
  setSections, instr, setInstr, busySec, voiceBusy, setVoiceBusy, reportPath,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: sec.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    boxShadow: isDragging ? "0 8px 20px rgba(6, 182, 212, 0.25)" : undefined,
    zIndex: isDragging ? 20 : "auto",
  };
  const _imgInputRef = useRef(null);
  const _imgBesideInputRef = useRef(null);
  const _pdfInputRef = useRef(null);
  // Iter 427.p57.h (2026-02-24) — Per-card state for the two new
  // brainstorm-aware entry points on this section card:
  //   • `useBrainstormCtx` — toggle beside the Refine button. When
  //     on, the refine POST includes `use_brainstorm_context: true`
  //     and the backend prepends the full brainstorm transcript +
  //     every uploaded attachment (e.g. an 8-page water analysis PDF)
  //     to the LLM prompt. Scope stays surgical: only this section's
  //     body changes.
  //   • `seedComposer` — inline composer that opens from the "+ Add
  //     section from brainstorm…" dropdown item. Collects a heading
  //     + instruction, then POSTs to /sections with `seed_instruction`
  //     so the new section is inserted AND populated in one round
  //     trip.
  const [useBrainstormCtx, setUseBrainstormCtx] = useState(false);
  const [seedComposer, setSeedComposer] = useState({
    open: false,
    heading: "",
    instruction: "",
    busy: false,
  });
  // Iter 427.p57.f (2026-02-24) — Compute grouping metadata INLINE
  // (no useMemo). useMemo with `sections` as a dep is worthless here
  // because `sections` gets a new array identity on every parent
  // `setSections` call, so the memo recomputes anyway — and the
  // memoized OBJECT identity would then flip on every render, feeding
  // any downstream `useEffect` that includes it in deps. Inline
  // computation is cheap (2 array lookups + 3 boolean comparisons)
  // and identity-stable enough for React.memo below to short-circuit
  // most re-renders.
  const isImage = sec.kind === "image";
  const _prevSec = idx > 0 ? sections?.[idx - 1] : null;
  const _nextSec = idx < (sections?.length ?? 0) - 1 ? sections?.[idx + 1] : null;
  const nextIsImage = _nextSec?.kind === "image";
  const groupedWithPrev = isImage && !!sec.row_group && sec.row_group === _prevSec?.row_group;
  const groupedWithNext = isImage && !!sec.row_group && sec.row_group === _nextSec?.row_group;
  const isInRow = groupedWithPrev || groupedWithNext;
  const canGroupWithNext = isImage && nextIsImage && !groupedWithNext;

  // Stable `onPatch` — only re-created when sec.id changes. Fixed
  // React error #185 in the p57.d iter; kept as-is here.
  const onImagePatch = useCallback(
    (patch) => {
      setSections((prev) =>
        prev.map((s) => (s.id === sec.id ? { ...s, ...patch } : s)),
      );
      patchSection(sec.id, patch);
    },
    // patchSection isn't stable but the closure just calls it — the
    // identity flip doesn't matter for correctness. Excluding it from
    // deps to keep this callback identity-stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sec.id],
  );
  // toggleRowGroup can't be memoised meaningfully — it reads current
  // `sections`, `sec.row_group`, `nextSec` on click — so we keep it
  // as a plain function. React.memo on the card wraps most of the
  // re-render cost anyway.
  const toggleRowGroup = async () => {
    if (!isImage) return;
    if (isInRow) {
      const grp = sec.row_group;
      await patchSection(sec.id, { row_group: null });
      const peers = (sections || []).filter(
        (x) => x.id !== sec.id && x.row_group === grp,
      );
      if (peers.length === 1) {
        await patchSection(peers[0].id, { row_group: null });
      }
      return;
    }
    if (!canGroupWithNext) return;
    const grp = _nextSec.row_group
      || `row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    if (!sec.row_group || sec.row_group !== grp) {
      await patchSection(sec.id, { row_group: grp });
    }
    if (!_nextSec.row_group) {
      await patchSection(_nextSec.id, { row_group: grp });
    }
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-md border p-2.5 space-y-2 ${
        isInRow
          ? "border-cyan-400/40 bg-cyan-500/[0.04]"
          : "border-white/10 bg-black/20"
      }`}
      data-testid={`workbench-report-section-${sec.id}`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          data-testid={`workbench-section-drag-${sec.id}`}
          className="p-1 rounded hover:bg-white/10 text-[#a0aabf] cursor-grab active:cursor-grabbing touch-none"
          title="Drag to reorder"
          aria-label={`Reorder section ${idx + 1} of ${total}`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <SectionTitleInput
          sec={sec}
          idx={idx}
          isInRow={isInRow}
          onCommit={(nextTitle) => patchSection(sec.id, { title: nextTitle })}
          setSections={setSections}
        />
        {sec.page_break_before && (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-cyan-400/40 text-cyan-200" data-testid={`workbench-section-newpage-tag-${sec.id}`}>
            New page
          </span>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          {isImage && (
            <button
              type="button"
              onClick={toggleRowGroup}
              disabled={!isInRow && !canGroupWithNext}
              data-testid={`workbench-section-row-toggle-${sec.id}`}
              title={
                isInRow
                  ? "Ungroup from row"
                  : canGroupWithNext
                    ? "Group side-by-side with the next image"
                    : "Group is only available when the next section is also an image"
              }
              className={`p-1 rounded transition ${
                isInRow
                  ? "hover:bg-cyan-500/20 text-cyan-100"
                  : canGroupWithNext
                    ? "hover:bg-cyan-500/10 text-cyan-200/80"
                    : "text-white/20 cursor-not-allowed"
              }`}
            >
              {isInRow
                ? <Unlink2 className="w-3 h-3" />
                : <Link2 className="w-3 h-3" />}
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid={`workbench-section-insert-after-${sec.id}`}
                className="p-1 rounded hover:bg-white/10 text-cyan-200"
                title="Insert below"
                aria-label="Insert section, image or PDF below"
              >
                <Plus className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#0f172a] border-white/10 text-cyan-50 min-w-[10rem]"
              data-testid={`workbench-section-insert-menu-${sec.id}`}
            >
              <DropdownMenuItem
                onSelect={() => addSection(idx + 1)}
                data-testid={`workbench-section-insert-section-${sec.id}`}
                className="cursor-pointer focus:bg-cyan-500/15"
              >
                <Plus className="w-3 h-3 mr-2 text-cyan-200" />
                Add section below
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setSeedComposer((p) => ({
                  ...p, open: true, heading: "", instruction: "",
                }))}
                data-testid={`workbench-section-insert-brainstorm-${sec.id}`}
                className="cursor-pointer focus:bg-fuchsia-500/15"
              >
                <Sparkles className="w-3 h-3 mr-2 text-fuchsia-200" />
                Add section from brainstorm…
              </DropdownMenuItem>
              {isImage && (
                <DropdownMenuItem
                  onSelect={() => _imgBesideInputRef.current?.click()}
                  data-testid={`workbench-section-insert-image-beside-${sec.id}`}
                  className="cursor-pointer focus:bg-cyan-500/15"
                >
                  <Link2 className="w-3 h-3 mr-2 text-cyan-200" />
                  Add image beside · same row
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => _imgInputRef.current?.click()}
                data-testid={`workbench-section-insert-image-${sec.id}`}
                className="cursor-pointer focus:bg-emerald-500/15"
              >
                <ImagePlus className="w-3 h-3 mr-2 text-emerald-200" />
                Add image below
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => addSection(idx + 1, { kind: "image_grid" })}
                data-testid={`workbench-section-insert-grid-${sec.id}`}
                className="cursor-pointer focus:bg-emerald-500/15"
              >
                <ImagePlus className="w-3 h-3 mr-2 text-emerald-200" />
                Add image grid below (side by side)
              </DropdownMenuItem>
              {!sections?.some((s) => s.kind === "cover") && (
                <DropdownMenuItem
                  onSelect={() => addSection(0, { kind: "cover" })}
                  data-testid={`workbench-section-insert-cover-${sec.id}`}
                  className="cursor-pointer focus:bg-cyan-500/15"
                >
                  <FileText className="w-3 h-3 mr-2 text-cyan-200" />
                  Add cover page
                </DropdownMenuItem>
              )}
              {!sections?.some((s) => s.kind === "contents") && (
                <DropdownMenuItem
                  onSelect={() => addSection(0, { kind: "contents" })}
                  data-testid={`workbench-section-insert-contents-${sec.id}`}
                  className="cursor-pointer focus:bg-cyan-500/15"
                >
                  <FileText className="w-3 h-3 mr-2 text-cyan-200" />
                  Add contents (table of contents)
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => _pdfInputRef.current?.click()}
                data-testid={`workbench-section-insert-pdf-${sec.id}`}
                className="cursor-pointer focus:bg-fuchsia-500/15"
              >
                <FileImage className="w-3 h-3 mr-2 text-fuchsia-200" />
                Import PDF below
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => onImagePatch({ page_break_before: !sec.page_break_before })}
                data-testid={`workbench-section-toggle-newpage-${sec.id}`}
                className="cursor-pointer focus:bg-cyan-500/15"
              >
                <FileText className="w-3 h-3 mr-2 text-cyan-200" />
                {sec.page_break_before ? "Remove new page" : "Start this section on a new page"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            ref={_imgInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`workbench-section-insert-image-input-${sec.id}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f && addImageSection) addImageSection(f, idx + 1);
            }}
          />
          {/* Iter 427.p57.e — "Add image beside" hidden input. On
              pick, we:
                1. Ensure the current section has a row_group (mint
                   one if it's the first image joining a row).
                2. Insert the new image at idx+1 with the SAME
                   row_group so the PDF renderer wraps both in a
                   single flex row.
                3. If the current section didn't have a row_group
                   yet, patch it in-place afterwards.
              Result: two consecutive image sections sharing a
              row_group — the row appears in the exported PDF once
              the user regenerates. */}
          <input
            ref={_imgBesideInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`workbench-section-insert-image-beside-input-${sec.id}`}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f || !addImageSection) return;
              const grp = sec.row_group
                || `row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
              await addImageSection(f, idx + 1, {
                row_group: grp,
                width_pct: sec.width_pct || 50,
                layout: "block",
              });
              if (!sec.row_group) {
                await patchSection(sec.id, { row_group: grp });
              }
            }}
          />
          <input
            ref={_pdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid={`workbench-section-insert-pdf-input-${sec.id}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f && importPdfSections) importPdfSections(f, sec.id);
            }}
          />
          <button
            type="button"
            onClick={() => deleteSection(sec.id)}
            data-testid={`workbench-section-delete-${sec.id}`}
            className="p-1 rounded hover:bg-rose-500/20 text-rose-200"
            title="Delete section"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      {sec.kind !== "cover" && <input
        type="text"
        value={sec.heading || ""}
        onChange={(e) =>
          setSections((prev) =>
            prev.map((s) => (s.id === sec.id ? { ...s, heading: e.target.value } : s)),
          )
        }
        onBlur={(e) => patchSection(sec.id, { heading: e.target.value })}
        placeholder={
          sec.kind === "image" || sec.kind === "image_grid"
            ? "(optional image caption / title)"
            : "(no heading — intro paragraph)"
        }
        data-testid={`workbench-section-heading-${sec.id}`}
        className="w-full bg-transparent border-b border-white/10 focus:border-cyan-400/40 outline-none text-sm text-[#e0e7f0] font-medium"
      />}
      {sec.kind === "cover" ? (
        <CoverEditor sec={sec} onPatch={onImagePatch} reportPath={reportPath} />
      ) : sec.kind === "contents" ? (
        <ContentsView sec={sec} sections={sections} />
      ) : sec.kind === "image_grid" ? (
        <ImageGridEditor sec={sec} onPatch={onImagePatch} />
      ) : sec.kind === "image" ? (
        <ImageSectionEditor
          sec={sec}
          onPatch={onImagePatch}
          canGroupWithNext={canGroupWithNext}
          isInRow={isInRow}
          onToggleRowGroup={toggleRowGroup}
        />
      ) : (
        <DictTextarea
          value={sec.body || ""}
          onChange={(e) =>
            setSections((prev) =>
              prev.map((s) => (s.id === sec.id ? { ...s, body: e.target.value } : s)),
            )
          }
          onBlur={(e) => patchSection(sec.id, { body: e.target.value })}
          data-testid={`workbench-section-body-${sec.id}`}
          className="w-full bg-black/25 rounded p-2 text-[11px] text-[#cbd5e1] font-mono resize-y min-h-[80px] outline-none focus:ring-1 focus:ring-cyan-400/30"
        />
      )}
      {!["image", "image_grid", "cover", "contents"].includes(sec.kind) && (
        <div className="space-y-1.5">
          <div className="flex gap-1.5 items-start">
            <input
              type="text"
              value={instr[sec.id] || ""}
              onChange={(e) => setInstr((p) => ({ ...p, [sec.id]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") refineSection(sec.id, { useBrainstormCtx });
              }}
              placeholder={useBrainstormCtx
                ? "Refine using brainstorm context (e.g. 'summarise the water analysis and cite its 3 dominant findings')…"
                : "Refine this section (e.g. more concise, bullet by stage)…"}
              disabled={busySec === sec.id}
              data-testid={`workbench-section-refine-input-${sec.id}`}
              className={`flex-1 min-w-0 bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#cbd5e1] outline-none border ${
                useBrainstormCtx
                  ? "border-fuchsia-400/30 focus:ring-1 focus:ring-fuchsia-400/40"
                  : "border-amber-400/20 focus:ring-1 focus:ring-amber-400/30"
              }`}
            />
            <VoiceDictation
              disabled={busySec === sec.id}
              onStateChange={(s) => setVoiceBusy((prev) => ({ ...prev, [sec.id]: !!s.busy }))}
              onText={(t) => setInstr((p) => ({
                ...p,
                [sec.id]: (p[sec.id] ? `${p[sec.id].trimEnd()} ${t}` : t),
              }))}
              testId={`workbench-section-refine-mic-${sec.id}`}
            />
            <button
              type="button"
              onClick={() => refineSection(sec.id, { useBrainstormCtx })}
              disabled={busySec === sec.id || !(instr[sec.id] || "").trim() || voiceBusy[sec.id]}
              data-testid={`workbench-section-refine-btn-${sec.id}`}
              className={`text-[10px] px-2 py-1.5 rounded border transition disabled:opacity-40 ${
                useBrainstormCtx
                  ? "border-fuchsia-400/50 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100"
                  : "border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-100"
              }`}
            >
              {busySec === sec.id
                ? "Refining…"
                : useBrainstormCtx ? "Refine + ctx" : "Refine"}
            </button>
          </div>
          {/* Iter 427.p57.h — Brainstorm-context toggle. Off by
              default (keeps fast refine fast). When on, the refine
              call includes the full brainstorm transcript + every
              uploaded attachment as reference material. Scope stays
              surgical (only this section body changes). */}
          <label
            className="flex items-center gap-1.5 text-[10px] text-[#a0aabf] hover:text-fuchsia-200 cursor-pointer select-none w-fit"
            data-testid={`workbench-section-refine-ctx-toggle-${sec.id}`}
          >
            <input
              type="checkbox"
              checked={useBrainstormCtx}
              onChange={(e) => setUseBrainstormCtx(e.target.checked)}
              className="accent-fuchsia-400 h-3 w-3"
              data-testid={`workbench-section-refine-ctx-checkbox-${sec.id}`}
            />
            <Sparkles className="w-2.5 h-2.5 text-fuchsia-300" />
            Use brainstorm context (transcript + all attachments)
          </label>
        </div>
      )}
      {/* Iter 427.p57.h — Inline composer for "Add section from
          brainstorm…". Opens from the "+" dropdown; grabs a heading
          + instruction and calls addSection() with seedInstruction
          so the backend generates the body grounded in the full
          brainstorm. Every OTHER section stays byte-identical — the
          scope contract is the same as per-section refine. */}
      {seedComposer.open && (
        <div
          className="mt-2 rounded-md border border-fuchsia-400/40 bg-fuchsia-500/[0.06] p-2 space-y-1.5"
          data-testid={`workbench-section-seed-composer-${sec.id}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-fuchsia-200/80 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            New section from brainstorm — inserts below "{sec.heading || "(intro)"}"
          </div>
          <input
            type="text"
            value={seedComposer.heading}
            onChange={(e) => setSeedComposer((p) => ({ ...p, heading: e.target.value }))}
            placeholder="Section heading (e.g. Water analysis)"
            disabled={seedComposer.busy}
            data-testid={`workbench-section-seed-heading-${sec.id}`}
            className="w-full bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-fuchsia-400/40 border border-fuchsia-400/20"
          />
          <DictTextarea
            value={seedComposer.instruction}
            onChange={(e) => setSeedComposer((p) => ({ ...p, instruction: e.target.value }))}
            placeholder="Instruction — what to write, grounded in the brainstorm (e.g. 'Summarise the water analysis PDF, calling out the 3 parameters most material to the recommendation and how each is addressed by the treatment train')…"
            disabled={seedComposer.busy}
            data-testid={`workbench-section-seed-instruction-${sec.id}`}
            className="w-full bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-fuchsia-400/40 border border-fuchsia-400/20 min-h-[70px] resize-y"
          />
          <div className="flex gap-1.5 justify-end pt-0.5">
            <button
              type="button"
              disabled={seedComposer.busy}
              onClick={() => setSeedComposer((p) => ({ ...p, open: false }))}
              data-testid={`workbench-section-seed-cancel-${sec.id}`}
              className="text-[10px] px-2 py-1 rounded border border-white/15 bg-black/20 hover:bg-black/40 text-[#a0aabf] inline-flex items-center gap-1 disabled:opacity-40"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
            <button
              type="button"
              disabled={seedComposer.busy || !seedComposer.instruction.trim()}
              onClick={async () => {
                setSeedComposer((p) => ({ ...p, busy: true }));
                await addSection(idx + 1, {
                  heading: seedComposer.heading.trim() || "New section",
                  seedInstruction: seedComposer.instruction.trim(),
                });
                setSeedComposer({
                  open: false, heading: "", instruction: "", busy: false,
                });
              }}
              data-testid={`workbench-section-seed-submit-${sec.id}`}
              className="text-[10px] px-2 py-1 rounded border border-fuchsia-400/50 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100 disabled:opacity-40 inline-flex items-center gap-1"
            >
              {seedComposer.busy
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Sparkles className="w-3 h-3" />}
              Generate section
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionTitleInput({ sec, idx, isInRow, onCommit, setSections }) {
  // Iter 427.p57.f (2026-02-25) — Inline-editable section title
  // shown in place of the auto-generated "{n}. {kind}" breadcrumb.
  // Falls back to `sec.kind` (uppercased) when empty. Uses the same
  // local-state + onBlur pattern as heading/caption to avoid the
  // per-keystroke round-trip issue.
  const fallback = String(sec.kind || "section").toUpperCase();
  const [draft, setDraft] = React.useState(sec.title || "");
  const focused = React.useRef(false);
  React.useEffect(() => {
    if (!focused.current) setDraft(sec.title || "");
  }, [sec.id, sec.title]);
  return (
    <span
      className="text-[9px] uppercase tracking-wider text-[#647087] flex items-center gap-1 min-w-0"
      data-testid={`workbench-section-kind-${sec.id}`}
    >
      <span className="shrink-0">{idx + 1}.</span>
      <input
        type="text"
        value={draft}
        placeholder={fallback}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          // Keep the local sections buffer in sync so re-renders don't
          // clobber the input value while the field is still focused.
          setSections((prev) =>
            prev.map((s) => (s.id === sec.id ? { ...s, title: v } : s)),
          );
        }}
        onFocus={() => { focused.current = true; }}
        onBlur={(e) => {
          focused.current = false;
          const next = (e.target.value || "").trim();
          if (next !== (sec.title || "")) {
            onCommit(next);
          }
        }}
        maxLength={80}
        data-testid={`workbench-section-title-input-${sec.id}`}
        title="Rename this section"
        className="bg-transparent outline-none text-[9px] uppercase tracking-wider text-[#a0aabf] placeholder-[#647087] focus:text-cyan-100 hover:text-[#cbd5e1] border-b border-transparent focus:border-cyan-400/40 min-w-0 flex-1 truncate"
      />
      {isInRow && (
        <span
          className="ml-1 px-1 py-[1px] rounded bg-cyan-500/20 text-cyan-100 text-[8px] tracking-widest shrink-0"
          data-testid={`workbench-section-row-badge-${sec.id}`}
        >
          ROW
        </span>
      )}
    </span>
  );
}

function ImageSectionEditor({
  sec, onPatch,
  canGroupWithNext = false, isInRow = false, onToggleRowGroup = null,
}) {
  const src = sec.upload_id ? uploadUrl(sec.upload_id) : "";
  const [imgErr, setImgErr] = React.useState(false);
  // Iter 427.p57.e (2026-02-25) — Local state for caption + wrap body.
  // Previously these fired `onPatch` on every keystroke, which round-tripped
  // to the backend and replaced the whole `sections` array from the server
  // response, wiping out characters the user was mid-typing (Dirk report:
  // "type one letter, wait 3-4s, type next"). Now we keep an optimistic
  // local buffer during typing and only commit via `onPatch` on blur —
  // mirroring the heading/body onBlur pattern in SortableSectionCard.
  const [captionDraft, setCaptionDraft] = React.useState(sec.caption || "");
  const [bodyDraft, setBodyDraft] = React.useState(sec.body || "");
  const captionFocused = React.useRef(false);
  const bodyFocused = React.useRef(false);
  // Sync from server when the section identity or upstream value changes
  // and the field is NOT currently focused (avoid clobbering active typing).
  React.useEffect(() => {
    if (!captionFocused.current) setCaptionDraft(sec.caption || "");
  }, [sec.id, sec.caption]);
  React.useEffect(() => {
    if (!bodyFocused.current) setBodyDraft(sec.body || "");
  }, [sec.id, sec.body]);
  const widthPresets = [25, 50, 60, 75, 100];
  const currentWidth = Number(sec.width_pct || 60);
  const layout = sec.layout || "block";
  const isWrap = layout === "text-right" || layout === "text-left";
  // Iter 427.p57 — Layout labels + icons (kept text-only to avoid
  // a new lucide dependency). Order mirrors the PDF render precedence.
  const layouts = [
    { key: "block",      label: "Block",   hint: "Image on its own line, caption below." },
    { key: "text-right", label: "Text →",  hint: "Image floats left, text wraps on the right." },
    { key: "text-left",  label: "Text ←",  hint: "Image floats right, text wraps on the left." },
    { key: "caption",    label: "Caption", hint: "Centered image with caption below." },
  ];
  return (
    <div className="space-y-2" data-testid={`workbench-section-image-${sec.id}`}>
      <div
        className="rounded border border-white/10 bg-black/25 p-2"
        style={{ minHeight: 80 }}
      >
        {src && !imgErr ? (
          <div style={{ overflow: "auto" }}>
            {isWrap && (
              <img
                src={src}
                alt={sec.caption || "Report image"}
                style={{
                  width: `${currentWidth}%`,
                  height: "auto",
                  float: layout === "text-right" ? "left" : "right",
                  [layout === "text-right" ? "marginRight" : "marginLeft"]: 10,
                  marginTop: 2,
                  marginBottom: 2,
                }}
                className="rounded"
                data-testid={`workbench-section-image-preview-${sec.id}`}
                onError={() => setImgErr(true)}
              />
            )}
            {!isWrap && (
              <div style={{ textAlign: sec.align || "center" }}>
                <img
                  src={src}
                  alt={sec.caption || "Report image"}
                  style={{ width: `${currentWidth}%`, height: "auto", maxWidth: "100%" }}
                  className="rounded"
                  data-testid={`workbench-section-image-preview-${sec.id}`}
                  onError={() => setImgErr(true)}
                />
              </div>
            )}
            {isWrap && (sec.body || "").trim() && (
              <div
                className="text-[11px] text-[#cbd5e1] leading-relaxed whitespace-pre-wrap"
                style={{ padding: "2px 0" }}
              >
                {sec.body}
              </div>
            )}
            <div style={{ clear: "both" }} />
          </div>
        ) : (
          <div className="text-[11px] text-rose-300/80 py-4 italic text-center">
            {imgErr ? "Image failed to load." : "No image attached yet."}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-wider text-[#647087]">
            Width · {currentWidth}%
          </div>
          <div className="flex gap-0.5 flex-wrap">
            {widthPresets.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => onPatch({ width_pct: w })}
                data-testid={`workbench-section-image-width-${w}-${sec.id}`}
                className={`text-[10px] px-1.5 py-0.5 rounded border transition ${
                  currentWidth === w
                    ? "border-cyan-400 bg-cyan-500/20 text-cyan-100"
                    : "border-white/10 bg-white/[0.02] text-[#a0aabf] hover:bg-white/[0.06]"
                }`}
              >
                {w}%
              </button>
            ))}
          </div>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={currentWidth}
            onChange={(e) => onPatch({ width_pct: Number(e.target.value) })}
            data-testid={`workbench-section-image-slider-${sec.id}`}
            className="w-full accent-cyan-400"
          />
        </div>
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-wider text-[#647087]">Align</div>
          <div className="flex gap-0.5">
            {["left", "center", "right"].map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => onPatch({ align: a })}
                data-testid={`workbench-section-image-align-${a}-${sec.id}`}
                disabled={isWrap}
                title={isWrap ? "Align is controlled by the text-wrap layout." : ""}
                className={`text-[10px] px-1.5 py-0.5 rounded border capitalize transition ${
                  isWrap
                    ? "border-white/5 bg-white/[0.02] text-[#4a5266] cursor-not-allowed"
                    : (sec.align || "center") === a
                      ? "border-cyan-400 bg-cyan-500/20 text-cyan-100"
                      : "border-white/10 bg-white/[0.02] text-[#a0aabf] hover:bg-white/[0.06]"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Iter 427.p57 — Layout picker. */}
      <div className="space-y-1">
        <div className="text-[9px] uppercase tracking-wider text-[#647087]">Layout</div>
        <div className="grid grid-cols-4 gap-0.5">
          {layouts.map((l) => (
            <button
              key={l.key}
              type="button"
              onClick={() => onPatch({ layout: l.key })}
              title={l.hint}
              data-testid={`workbench-section-image-layout-${l.key}-${sec.id}`}
              className={`text-[10px] px-1.5 py-0.5 rounded border transition ${
                layout === l.key
                  ? "border-cyan-400 bg-cyan-500/20 text-cyan-100"
                  : "border-white/10 bg-white/[0.02] text-[#a0aabf] hover:bg-white/[0.06]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      {/* Iter 427.p57 — Wrap-text markdown body (only when layout is
          text-right or text-left). Reuses the section's `body` field.
          Iter 427.p57.d (Dirk, 2026-02-24) — Explicit label so it's
          obvious this field renders as flowing paragraph text NEXT to
          the image, not as a caption. */}
      {isWrap && (
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-wider text-[#647087] flex items-center justify-between">
            <span>Wrap text · beside the image</span>
            <span className="text-[8px] normal-case tracking-normal text-[#647087]/70">
              Markdown supported
            </span>
          </div>
          <DictTextarea
            value={bodyDraft}
            placeholder="Paragraph text that will wrap alongside the image…"
            onChange={(e) => setBodyDraft(e.target.value)}
            onFocus={() => { bodyFocused.current = true; }}
            onBlur={(e) => {
              bodyFocused.current = false;
              if ((e.target.value || "") !== (sec.body || "")) {
                onPatch({ body: e.target.value });
              }
            }}
            data-testid={`workbench-section-image-body-${sec.id}`}
            rows={4}
            className="w-full bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#e2e8f0] border border-white/10 outline-none focus:ring-1 focus:ring-cyan-400/30 leading-relaxed"
          />
        </div>
      )}
      <div className="space-y-1">
        <div className="text-[9px] uppercase tracking-wider text-[#647087]">
          Caption · under the image
        </div>
        <input
          type="text"
          value={captionDraft}
          placeholder="Small caption printed directly under the image (optional)"
          onChange={(e) => setCaptionDraft(e.target.value)}
          onFocus={() => { captionFocused.current = true; }}
          onBlur={(e) => {
            captionFocused.current = false;
            if ((e.target.value || "") !== (sec.caption || "")) {
              onPatch({ caption: e.target.value });
            }
          }}
          data-testid={`workbench-section-image-caption-${sec.id}`}
          className="w-full bg-black/25 rounded px-2 py-1.5 text-[11px] text-[#cbd5e1] border border-white/10 outline-none focus:ring-1 focus:ring-cyan-400/30"
        />
      </div>
      {/* Iter 427.p57.d — Row-group inline hint. Only shown when the
          NEXT section is also an image and this section isn't yet
          grouped. Nudges the user toward the toolbar toggle instead
          of leaving them stranded wondering why two images won't sit
          side-by-side. */}
      {(canGroupWithNext || isInRow) && onToggleRowGroup && (
        <button
          type="button"
          onClick={onToggleRowGroup}
          data-testid={`workbench-section-image-row-hint-${sec.id}`}
          className={`w-full text-left text-[10px] rounded px-2 py-1.5 border transition ${
            isInRow
              ? "border-cyan-400/40 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/15"
              : "border-white/10 bg-white/[0.02] text-[#a0aabf] hover:bg-white/[0.06]"
          }`}
        >
          {isInRow ? (
            <>
              <Unlink2 className="w-3 h-3 inline mr-1.5 -mt-0.5" />
              This image is in a row · click to ungroup
            </>
          ) : (
            <>
              <Link2 className="w-3 h-3 inline mr-1.5 -mt-0.5" />
              Place side-by-side with the next image
            </>
          )}
        </button>
      )}
    </div>
  );
}

